import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { 
  X, ChevronRight, ChevronLeft, 
  Check, Building2, Calendar, CreditCard, Shield,
  Sparkles, Star, Play, Pause, Target, TrendingUp,
  BarChart3, Users, FileCheck, Clock, Award,
  Quote, Share2, Mail, MessageCircle, ArrowRight,
  Lightbulb, Zap, Eye, Calculator, Brain, PlusCircle,
  FileSignature, Send, Lock, CheckCircle2, FileText,
  GraduationCap, Wifi, MonitorSmartphone, ShieldCheck, HeartHandshake
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

// Logo Studio - RIMOSSO, useremo testo
// const STUDIO_LOGO = "https://customer-assets.emergentagent.com/job_5f4b7e5e-1828-4505-8219-4bbc337dfd25/artifacts/fl4j9q7j_Color%20logo%20-%20no%20background.png";

// Immagine Google Reviews
const GOOGLE_REVIEWS_IMAGE = "https://customer-assets.emergentagent.com/job_6283a075-fe18-448a-8d16-b7362025cb10/artifacts/0mzouafk_IMG_7276.JPG";

// Tutti i servizi strategici disponibili
const SERVIZI_STRATEGICI_DISPONIBILI = [
  { id: "consulenza_strategica", nome: "Consulenza Strategica Trimestrale", descrizione: "Controllo di gestione e pianificazione fiscale trimestrale" },
  { id: "verbali_soci_cda", nome: "Redazione Verbali Soci e CDA", descrizione: "Redazione verbali assemblee soci e consiglio di amministrazione" },
  { id: "pacchetto_ore_5", nome: "Pacchetto 5 ore consulenza", descrizione: "5 ore di consulenza incluse. Ore extra fatturate a €97/ora" },
  { id: "pacchetto_ore_10", nome: "Pacchetto 10 ore consulenza", descrizione: "10 ore di consulenza incluse. Ore extra fatturate a €97/ora" },
  { id: "pacchetto_ore_20", nome: "Pacchetto 20 ore consulenza", descrizione: "20 ore di consulenza incluse. Ore extra fatturate a €97/ora" },
  { id: "piattaforma_canone", nome: "Canone Piattaforma Gestionale", descrizione: "Licenza portale: analisi obiettivi, riunione strategica, verbale tutela crisi d'impresa, simulazioni" },
  { id: "piattaforma_setup", nome: "Setup Iniziale Piattaforma", descrizione: "Impostazione iniziale piattaforma gestionale" },
  { id: "consulenza_oraria", nome: "Consulenza Oraria Extra", descrizione: "Consulenza professionale a ore (oltre pacchetto)" }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const CATEGORY_COLORS = {
  contabilita: { color: "#1E40AF", name: "Contabilita" },
  fiscale: { color: "#BE185D", name: "Fiscale" },
  personale: { color: "#047857", name: "770 e CU" },
  consulenza: { color: "#7C3AED", name: "Consulenza" },
  extra: { color: "#0369A1", name: "Extra" },
  riscossione: { color: "#B45309", name: "Riscossione" }
};

// Mappa descrizioni dettagliate per ogni servizio
// Ogni descrizione spiega il valore e indica se è un obbligo di legge
const getServizioDescrizione = (nome) => {
  const n = nome.toLowerCase();
  
  // Contabilità
  if (n.includes('contabilit') && n.includes('ordinaria'))
    return { desc: "Registrazione di tutte le fatture, pagamenti e incassi. Significa avere sempre un quadro preciso di entrate e uscite, senza errori e senza ritardi.", obbligo: true };
  if (n.includes('contabilit') && n.includes('semplificata'))
    return { desc: "Gestione completa della contabilità semplificata: registrazioni, prima nota e libro giornale. Tutto in regola, senza pensieri.", obbligo: true };
  if (n.includes('liquidazion') && n.includes('iva'))
    return { desc: "Calcolo e versamento dell'IVA dovuta ogni mese o trimestre. Eviti sanzioni e interessi per ritardi o errori nel calcolo.", obbligo: true };
  if (n.includes('lipe'))
    return { desc: "Comunicazione trimestrale dei dati delle liquidazioni IVA all'Agenzia delle Entrate. Adempimento obbligatorio con pesanti sanzioni in caso di omissione.", obbligo: true };
  if (n.includes('bilancio'))
    return { desc: "Redazione del bilancio d'esercizio con nota integrativa e deposito al Registro Imprese. È il documento che racconta lo stato di salute della tua azienda e un obbligo inderogabile.", obbligo: true };
  if (n.includes('dich') && n.includes('iva'))
    return { desc: "Dichiarazione annuale IVA: riepilogo di tutta l'IVA a debito e a credito dell'anno. Fondamentale per la chiusura fiscale e obbligatoria per legge.", obbligo: true };
  if (n.includes('redditi'))
    return { desc: "Calcolo e dichiarazione delle imposte sul reddito (IRES/IRPEF) e IRAP. È qui che si determina quanto paghi di tasse: farlo bene significa non pagare un euro in più del dovuto.", obbligo: true };
  if (n.includes('dichiarazion') && (n.includes('redditi') || n.includes('ires') || n.includes('irap')))
    return { desc: "Calcolo e dichiarazione delle imposte sul reddito (IRES/IRPEF) e IRAP. È qui che si determina quanto paghi di tasse: farlo bene significa non pagare un euro in più del dovuto.", obbligo: true };
  if (n.includes('770'))
    return { desc: "Modello 770: dichiarazione annuale delle ritenute operate su dipendenti, collaboratori e professionisti. Adempimento con scadenza tassativa.", obbligo: true };
  if (n.includes('certificazion') || n.includes('cu '))
    return { desc: "Certificazioni Uniche per dipendenti e collaboratori: attestano i compensi erogati e le ritenute applicate. Vanno consegnate entro scadenze precise.", obbligo: true };
  if (n.includes('f24'))
    return { desc: "Preparazione e invio telematico degli F24 per il versamento di imposte, contributi e tributi. Ci pensiamo noi, tu non devi ricordarti nessuna scadenza.", obbligo: true };
  if (n.includes('intrastat'))
    return { desc: "Modelli Intrastat per operazioni con l'UE: cessioni, acquisti e servizi intracomunitari. Obbligatorio per chi commercia con l'estero.", obbligo: true };
  if (n.includes('esterometro'))
    return { desc: "Comunicazione delle operazioni con soggetti esteri non documentate da fattura elettronica. Adempimento obbligatorio con cadenza trimestrale.", obbligo: true };
  if (n.includes('recupero') && (n.includes('corrispettivi') || n.includes('ade')))
    return { desc: "Recupero e riconciliazione dei corrispettivi dall'Agenzia delle Entrate. Garantiamo che tutti i dati siano allineati con le registrazioni contabili.", obbligo: true };
  if (n.includes('recupero') && n.includes('fattur'))
    return { desc: "Recupero delle fatture elettroniche dal cassetto fiscale dell'Agenzia delle Entrate. Ci assicuriamo che nessun documento vada perso.", obbligo: true };
  if (n.includes('registro') && n.includes('imprese'))
    return { desc: "Gestione delle pratiche al Registro Imprese: variazioni, aggiornamenti e depositi obbligatori.", obbligo: true };
  
  // Consulenza
  if (n.includes('consulenza strategica') || n.includes('controllo di gestione'))
    return { desc: "Ogni trimestre analizziamo insieme i tuoi numeri, definiamo obiettivi concreti e creiamo un piano d'azione. Non è teoria: è un metodo che ti dice cosa fare, quando e perché.", obbligo: false };
  if (n.includes('pianificazione fiscal'))
    return { desc: "Simuliamo in anticipo il carico fiscale e identifichiamo le strategie per ottimizzarlo legalmente. Sai già a luglio quanto pagherai a novembre.", obbligo: false };
  if (n.includes('verbali') && (n.includes('soci') || n.includes('cda')))
    return { desc: "Redazione professionale dei verbali di assemblea soci e consiglio di amministrazione. Documenti fondamentali per la governance aziendale.", obbligo: true };
  if (n.includes('piattaforma') || n.includes('pannello'))
    return { desc: "Accesso alla piattaforma di controllo direzionale: KPI in tempo reale, semafori su ogni area aziendale, simulazioni e obiettivi a cascata. Il tuo cruscotto per decidere con i numeri.", obbligo: false };
  if (n.includes('pacchetto') && n.includes('ore'))
    return { desc: "Ore di consulenza professionale incluse nel canone per affrontare temi specifici: contrattualistica, operazioni straordinarie, pareri tecnici. Le ore extra vengono fatturate a tariffa agevolata.", obbligo: false };
  if (n.includes('consulenza oraria'))
    return { desc: "Consulenza professionale su temi specifici, fatturata a ore. Per quesiti che vanno oltre l'assistenza ordinaria inclusa nel canone.", obbligo: false };
  
  // Extra
  if (n.includes('pos') || n.includes('collegamento'))
    return { desc: "Configurazione del collegamento tra POS e registratore telematico per l'invio automatico dei corrispettivi all'Agenzia delle Entrate.", obbligo: true };
  if (n.includes('spid') || n.includes('delega'))
    return { desc: "Attivazione della delega SPID per ricevere automaticamente tutte le fatture elettroniche. Zero lavoro manuale per te.", obbligo: false };
  if (n.includes('successione'))
    return { desc: "Predisposizione e presentazione della dichiarazione di successione. Pratica complessa che richiede competenza specifica.", obbligo: true };
  if (n.includes('imu'))
    return { desc: "Calcolo e predisposizione dei versamenti IMU sugli immobili. Ti avvisiamo delle scadenze e prepariamo gli F24.", obbligo: true };
  if (n.includes('contenzioso') || n.includes('ricorso'))
    return { desc: "Assistenza in caso di contenzioso tributario: analisi dell'avviso, valutazione delle opzioni e, se necessario, predisposizione del ricorso.", obbligo: false };
  if (n.includes('ravvedimento'))
    return { desc: "Regolarizzazione di adempimenti fiscali omessi o tardivi tramite ravvedimento operoso, riducendo al minimo sanzioni e interessi.", obbligo: false };
  if (n.includes('isa') || n.includes('questionario'))
    return { desc: "Compilazione e invio del questionario ISA (Indici Sintetici di Affidabilità fiscale). Un buon punteggio ISA ti protegge da controlli e accertamenti.", obbligo: true };
  if (n.includes('fattur') && n.includes('ester'))
    return { desc: "Gestione e invio delle fatture relative a operazioni con soggetti esteri. Adempimento obbligatorio per tutte le transazioni internazionali.", obbligo: true };
  if (n.includes('visura') || n.includes('camerale'))
    return { desc: "Richiesta e analisi di visure camerali per verifiche su fornitori, clienti o partner commerciali.", obbligo: false };
  
  // Default generico
  return { desc: "Gestione completa dell'adempimento con assistenza dedicata e rispetto di tutte le scadenze.", obbligo: false };
};

// Recensioni reali dello studio (settore invece del nome azienda)
const RECENSIONI = [
  {
    nome: "Marco R.",
    azienda: "Settore IT e Software",
    testo: "Professionalita e competenza eccezionali. Da quando ci affidiamo a SC Dottori Commercialisti, abbiamo ottimizzato la nostra gestione fiscale e ridotto significativamente i costi.",
    stelle: 5
  },
  {
    nome: "Laura B.",
    azienda: "Settore Ristorazione",
    testo: "Finalmente un commercialista che parla la mia lingua! Mi aiutano a capire i numeri e a prendere decisioni migliori per il mio ristorante.",
    stelle: 5
  },
  {
    nome: "Giovanni P.",
    azienda: "Settore Edilizia",
    testo: "Il controllo di gestione trimestrale ha cambiato il modo in cui guido la mia azienda. Ora ho sempre il polso della situazione.",
    stelle: 5
  },
  {
    nome: "Alessia M.",
    azienda: "Settore Legale",
    testo: "Disponibilita, precisione e soprattutto una visione strategica che va oltre la semplice contabilita. Consigliatissimi!",
    stelle: 5
  },
  {
    nome: "Roberto C.",
    azienda: "Settore Automotive",
    testo: "Mi hanno aiutato a uscire da una situazione complicata con l'Agenzia delle Entrate. Competenti e sempre presenti.",
    stelle: 5
  },
  {
    nome: "Francesca D.",
    azienda: "Settore Design e Architettura",
    testo: "La piattaforma di controllo gestione e fantastica. Vedo in tempo reale margini, cassa e obiettivi. Un vero game changer.",
    stelle: 5
  },
  {
    nome: "Antonio S.",
    azienda: "Settore Commercio al Dettaglio",
    testo: "Dopo anni di commercialisti 'tradizionali', finalmente ho trovato qualcuno che mi fa capire davvero come va la mia attivita. Consiglio vivamente!",
    stelle: 5
  },
  {
    nome: "Elena V.",
    azienda: "Settore E-commerce",
    testo: "Gestiscono la mia contabilita con precisione svizzera. Le simulazioni fiscali mi hanno fatto risparmiare migliaia di euro. Top!",
    stelle: 5
  },
  {
    nome: "Massimo L.",
    azienda: "Settore Manifatturiero",
    testo: "Professionalita impeccabile. Sempre disponibili, rispondono in tempi rapidissimi e soprattutto fanno capire le cose anche a chi non e del mestiere.",
    stelle: 5
  },
  {
    nome: "Chiara N.",
    azienda: "Settore Benessere e Spa",
    testo: "La riunione trimestrale con verbale operativo ha rivoluzionato il mio modo di gestire l'azienda. Ora so sempre cosa fare e quando.",
    stelle: 5
  },
  {
    nome: "Paolo M.",
    azienda: "Settore Logistica",
    testo: "Grazie a loro ho potuto pianificare l'espansione della mia azienda con numeri chiari e proiezioni affidabili. Partner eccezionale!",
    stelle: 5
  },
  {
    nome: "Sara T.",
    azienda: "Settore Moda e Abbigliamento",
    testo: "Finalmente un commercialista che non si limita a 'fare i conti' ma ti aiuta a far crescere il business. Scelta migliore che potessi fare.",
    stelle: 5
  },
  {
    nome: "Luca B.",
    azienda: "Settore Immobiliare",
    testo: "Competenza e disponibilita ai massimi livelli. Risolvono problemi prima ancora che si presentino. 5 stelle meritate.",
    stelle: 5
  },
  {
    nome: "Valentina C.",
    azienda: "Settore Consulenza",
    testo: "Ho cambiato commercialista dopo 15 anni e non potevo fare scelta migliore. Approccio moderno, tecnologia avanzata e tanta competenza.",
    stelle: 5
  },
  {
    nome: "Stefano R.",
    azienda: "Settore Agricolo",
    testo: "Mi seguono con attenzione nonostante le specificita del mio settore. Sempre aggiornati sulle normative e pronti a consigliarmi.",
    stelle: 5
  },
  {
    nome: "Maria G.",
    azienda: "Settore Sanitario",
    testo: "Precisione, puntualita e soprattutto un approccio umano che fa la differenza. Ci si sente seguiti davvero, non solo un numero.",
    stelle: 5
  }
];

const STUDIO_INFO = {
  nome: "SC Dottori Commercialisti SRL STP",
  formaGiuridica: "Unipersonale",
  sedeLegale: "Via Ponte Marino 41, Ravenna",
  salaRiunioni: "Via Ponte Marino 43, Ravenna",
  sedeAmministrativa: "Via Ponte Marino 41, Ravenna",
  cf: "02567720392",
  piva: "02567720392",
  sdi: "USAL8PV",
  pec: "scdottoricommercialisti@pec.it",
  amministratore: "Dott.ssa Silvia Casali",
  amministratoreCf: "CSLSLV79C66D704V",
  albo: "Albo dei Dottori Commercialisti e degli Esperti Contabili di Ravenna n. 511A",
  qualifica: "Revisore dei Conti",
  sediAmministrative: ["Ravenna", "Cervia", "Forlì", "Roma", "Latina", "Ciampino", "Bari"]
};

const PUNTI_FORZA_STUDIO = [
  {
    titolo: "20+ anni di esperienza",
    descrizione: "Dal 2003 affianchiamo imprenditori e professionisti con competenza e dedizione"
  },
  {
    titolo: "Approccio a 360 gradi",
    descrizione: "Non solo numeri: strategia, marketing, pianificazione fiscale e controllo di gestione"
  },
  {
    titolo: "Rete di partner specializzati",
    descrizione: "Avvocati, notai, consulenti del lavoro, agenzie marketing: un team completo per ogni esigenza"
  },
  {
    titolo: "Presenza nazionale",
    descrizione: "6 sedi operative per essere sempre vicini ai nostri clienti, ovunque si trovino"
  },
  {
    titolo: "Formazione continua",
    descrizione: "Aggiornamento costante con i migliori formatori europei in strategia e business"
  },
  {
    titolo: "Tecnologia avanzata",
    descrizione: "Piattaforma digitale per KPI, simulazioni e decisioni in tempo reale"
  }
];

export default function PresentationProfessional({ preventivo, onClose, clienteEsistente = false, isPublicView = false }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [slideKey, setSlideKey] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const autoPlayRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const contentRef = useRef(null);
  const SLIDE_DURATION = 8000; // 8 secondi per slide

  // Scaglioni contabilità
  const [scaglioniContabilita, setScaglioniContabilita] = useState([]);
  const [tipoContabilita, setTipoContabilita] = useState("");

  // Carica gli scaglioni dalla stessa tipologia di contabilità presente nel preventivo
  useEffect(() => {
    const servizioContabilita = preventivo.servizi.find(s => 
      s.categoria === 'contabilita' && (
        s.nome.toLowerCase().includes('contabilità') || 
        s.nome.toLowerCase().includes('contabilita')
      )
    );
    if (!servizioContabilita) return;

    const nome = servizioContabilita.nome.toLowerCase();
    let subcat = '';
    if (nome.includes('ordinaria')) subcat = 'ordinaria';
    else if (nome.includes('semplificata')) subcat = 'semplificata';
    else if (nome.includes('forfett')) subcat = 'forfettaria';
    if (!subcat) return;

    setTipoContabilita(subcat);

    fetch(`${API}/tariffe/contabilita`)
      .then(res => res.json())
      .then(data => {
        const sottocategoria = data?.sottocategorie?.[subcat];
        if (sottocategoria?.servizi) {
          setScaglioniContabilita(sottocategoria.servizi);
        }
      })
      .catch(() => {});
  }, [preventivo.servizi]);
  
  // Stato per l'accettazione preventivo
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptanceEmail, setAcceptanceEmail] = useState(preventivo?.cliente?.email || "");
  const [signerFirstName, setSignerFirstName] = useState(preventivo?.cliente?.legale_rappresentante?.split(' ')[0] || "");
  const [signerLastName, setSignerLastName] = useState(preventivo?.cliente?.legale_rappresentante?.split(' ').slice(1).join(' ') || "");
  const [signerCF, setSignerCF] = useState("");
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Touch handlers - swipe per navigare, tap per pausa
  const isTouchMoving = useRef(false);

  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };
    isTouchMoving.current = false;
  };

  const handleTouchMove = () => {
    isTouchMoving.current = true;
  };

  const handleTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    const elapsed = Date.now() - touchStartRef.current.time;
    
    // Swipe orizzontale (minimo 50px, più orizzontale che verticale, entro 500ms)
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5 && elapsed < 500) {
      setIsAutoPlay(false);
      if (deltaX < 0) {
        nextSlide(); // Swipe sinistra → avanti
      } else {
        prevSlide(); // Swipe destra → indietro
      }
      return;
    }
    
    // NON fare pausa se l'utente stava scrollando verticalmente
    if (isTouchMoving.current) return;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, []);

  // Servizi divisi per area - DEVE essere prima di slides
  const serviziAmministrativi = useMemo(() => {
    const cats = ['contabilita', 'fiscale', 'personale', 'extra', 'riscossione'];
    return preventivo.servizi.filter(s => cats.includes(s.categoria));
  }, [preventivo.servizi]);

  const serviziStrategici = useMemo(() => {
    return preventivo.servizi.filter(s => s.categoria === 'consulenza');
  }, [preventivo.servizi]);

  const hasServizioStrategico = serviziStrategici.length > 0;

  // Calcola i servizi strategici NON inclusi nel preventivo
  const serviziStrategiciNonInclusi = useMemo(() => {
    const nomiServiziInclusi = preventivo.servizi
      .filter(s => s.categoria === 'consulenza')
      .map(s => s.nome.toLowerCase());
    
    return SERVIZI_STRATEGICI_DISPONIBILI.filter(
      servizio => !nomiServiziInclusi.some(nome => 
        nome.includes(servizio.nome.toLowerCase()) || 
        servizio.nome.toLowerCase().includes(nome.split(' ')[0])
      )
    );
  }, [preventivo.servizi]);

  // Slide dinamiche - alcune solo se ci sono servizi strategici
  // Verifica se il preventivo include il Pannello di Controllo Direzionale
  const hasPannelloControllo = useMemo(() => {
    return preventivo.servizi.some(s => 
      s.nome.toLowerCase().includes('piattaforma') || 
      s.nome.toLowerCase().includes('pannello') ||
      s.id === 'piattaforma_canone'
    );
  }, [preventivo.servizi]);

  // Variabili per determinare il tipo di preventivo
  const hasContabilita = useMemo(() => {
    return preventivo.servizi.some(s => 
      s.categoria?.toLowerCase() === 'contabilità' ||
      s.categoria?.toLowerCase() === 'contabilita' ||
      s.nome?.toLowerCase().includes('contabil')
    );
  }, [preventivo.servizi]);

  const hasConsulenza = useMemo(() => {
    return preventivo.servizi.some(s => 
      s.categoria?.toLowerCase() === 'consulenza' ||
      s.nome?.toLowerCase().includes('consulenza')
    );
  }, [preventivo.servizi]);

  const soloUnaTantum = useMemo(() => {
    return preventivo.servizi.every(s => s.billing_type === 'one_off');
  }, [preventivo.servizi]);

  const slides = useMemo(() => {
    // =====================================================
    // TIPO 4: SOLO PRATICHE (servizi una tantum)
    // =====================================================
    if (soloUnaTantum) {
      return [
        { type: "copertina_esistente", title: "Copertina" },
        { type: "il_tuo_commercialista", title: "Il tuo professionista" },
        { type: "dettaglio_pratiche", title: "Pratiche richieste" },
        { type: "proposta", title: "Proposta economica" },
        { type: "tempistiche", title: "Tempistiche" },
        { type: "chiusura", title: "Call to action" }
      ];
    }

    // =====================================================
    // TIPO 3: CLIENTE ESISTENTE (qualsiasi servizio ricorrente)
    // =====================================================
    if (clienteEsistente) {
      return [
        { type: "copertina_esistente", title: "Copertina" },
        { type: "il_tuo_commercialista", title: "Il tuo professionista" },
        { type: "cosa_include_dettaglio", title: "Cosa è incluso" },
        { type: "proposta", title: "Proposta economica" },
        { type: "tempistiche", title: "Tempistiche" },
        { type: "chiusura", title: "Call to action" }
      ];
    }
    
    // =====================================================
    // TIPO 2: NUOVO CLIENTE - PACCHETTO COMPLETO
    // (contabilità + consulenza/pannello)
    // =====================================================
    if (hasContabilita && (hasConsulenza || hasPannelloControllo)) {
      const slideList = [
        { type: "copertina", title: "Copertina" },
        { type: "il_tuo_commercialista", title: "Il tuo professionista" },
        { type: "cosa_include_dettaglio", title: "Cosa è incluso" },
        { type: "noi_vs_altri", title: "Perché siamo diversi" },
        { type: "tecnologia", title: "Tecnologia" },
      ];
      if (hasPannelloControllo) {
        slideList.push({ type: "pannello_controllo", title: "Pannello di Controllo" });
      }
      slideList.push(
        { type: "proposta_canone", title: "Il tuo investimento" },
        { type: "garanzie", title: "Garanzie" },
        { type: "modalita_documenti", title: "Acquisizione documenti" },
        { type: "onboarding", title: "Come partiamo" },
        { type: "chiusura", title: "Iniziamo" }
      );
      return slideList;
    }

    // =====================================================
    // TIPO 1: NUOVO CLIENTE - SOLO CONTABILITÀ
    // =====================================================
    return [
      { type: "copertina", title: "Copertina" },
      { type: "il_tuo_commercialista", title: "Il tuo professionista" },
      { type: "cosa_include_dettaglio", title: "Cosa è incluso" },
      { type: "noi_vs_altri", title: "Perché siamo diversi" },
      { type: "tecnologia", title: "Tecnologia" },
      { type: "proposta_canone", title: "Il tuo investimento" },
      { type: "upgrade", title: "Fai il salto" },
      { type: "garanzie", title: "Garanzie" },
      { type: "modalita_documenti", title: "Acquisizione documenti" },
      { type: "onboarding", title: "Come partiamo" },
      { type: "chiusura", title: "Iniziamo" }
    ];
  }, [hasContabilita, hasConsulenza, hasPannelloControllo, soloUnaTantum, clienteEsistente]);

  // Calcoli - allineati alla logica dei documenti PDF
  // Lo sconto si applica separatamente su ricorrenti e una tantum
  const subtotaleMonthly = useMemo(() => {
    return preventivo.subtotale_monthly || 
      preventivo.servizi.filter(s => s.billing_type === 'monthly').reduce((acc, s) => acc + (s.prezzo_totale || 0), 0);
  }, [preventivo]);

  const subtotaleOneOff = useMemo(() => {
    return preventivo.subtotale_one_off ||
      preventivo.servizi.filter(s => s.billing_type === 'one_off').reduce((acc, s) => acc + (s.prezzo_totale || 0), 0);
  }, [preventivo]);

  const scontoPct = preventivo.sconto_percentuale || 0;

  const nettoMensile = useMemo(() => {
    const scontoM = subtotaleMonthly * (scontoPct / 100);
    return (subtotaleMonthly - scontoM) / 12;
  }, [subtotaleMonthly, scontoPct]);

  const nettoAnnuale = useMemo(() => {
    const scontoM = subtotaleMonthly * (scontoPct / 100);
    return subtotaleMonthly - scontoM;
  }, [subtotaleMonthly, scontoPct]);

  // Verifica se ci sono servizi ricorrenti (monthly)
  const hasServiziRicorrenti = useMemo(() => {
    return preventivo.servizi.some(s => s.billing_type === 'monthly');
  }, [preventivo.servizi]);

  // Totale servizi una tantum (con sconto applicato)
  const totaleUnaTantum = useMemo(() => {
    const scontoO = subtotaleOneOff * (scontoPct / 100);
    return subtotaleOneOff - scontoO;
  }, [subtotaleOneOff, scontoPct]);

  // Funzioni per accettazione preventivo e invio documenti via email
  const handleSendForSignature = async () => {
    if (!signerFirstName || !signerLastName || !acceptanceEmail || !signerCF) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    // Validazione base del CF (16 caratteri alfanumerici)
    if (signerCF.length !== 16) {
      toast.error("Il Codice Fiscale deve essere di 16 caratteri");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/preventivi/accetta-e-invia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          preventivo_id: preventivo.id,
          recipient_email: acceptanceEmail,
          recipient_name: `${signerFirstName} ${signerLastName}`,
          recipient_cf: signerCF.toUpperCase(),
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setOtpVerified(true);
        toast.success("Documenti inviati con successo!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio dei documenti");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mantengo le vecchie funzioni per retrocompatibilità (non usate)
  const handleSendOtp = async () => {
    // Deprecato - ora usiamo invio email
    handleSendForSignature();
  };

  const handleVerifyOtp = async () => {
    // Deprecato - la verifica è manuale (cliente firma e rimanda)
    toast.info("Il cliente firmerà i documenti e li rimanderà via email");
  };

  const [upgradeRequested, setUpgradeRequested] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [selectedUpgrades, setSelectedUpgrades] = useState([]);
  
  const handleRequestUpgrade = async (upgradeType) => {
    try {
      const response = await fetch(`${API}/preventivi/richiedi-upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          preventivo_id: preventivo.id,
          cliente: preventivo.cliente.ragione_sociale,
          upgrade_tipo: upgradeType,
          email_cliente: acceptanceEmail || preventivo.cliente.email,
        })
      });
      if (response.ok) {
        setUpgradeRequested(true);
        setSelectedUpgrade(upgradeType);
        toast.success("Richiesta di upgrade inviata! Ti contatteremo a breve.");
      } else {
        toast.error("Errore nell'invio della richiesta");
      }
    } catch {
      toast.error("Errore di connessione");
    }
  };

  const downloadAllDocuments = async () => {
    const documents = [
      { url: `${API}/preventivi/${preventivo.id}/word`, name: `Preventivo_${preventivo.numero_preventivo}.docx` },
      { url: `${API}/preventivi/${preventivo.id}/lettera-incarico`, name: `Lettera_Incarico_${preventivo.numero_preventivo}.docx` },
      { url: `${API}/preventivi/${preventivo.id}/allegato-b`, name: `Allegato_B_Condizioni_${preventivo.numero_preventivo}.docx` },
      { url: `${API}/preventivi/${preventivo.id}/allegato-c`, name: `Allegato_C_Privacy_${preventivo.numero_preventivo}.docx` }
    ];
    
    for (const doc of documents) {
      try {
        const response = await fetch(doc.url);
        if (response.ok) {
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = doc.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }
      } catch (error) {
        console.error(`Error downloading ${doc.name}:`, error);
      }
      // Piccolo delay tra i download
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    toast.success("Tutti i documenti sono stati scaricati!");
  };

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setCurrentSlide(prev => prev + 1);
      setSlideKey(prev => prev + 1);
      setProgressKey(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, [currentSlide, isAnimating, slides.length]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrentSlide(prev => prev - 1);
      setSlideKey(prev => prev + 1);
      setProgressKey(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, [currentSlide, isAnimating]);

  // Auto-play
  useEffect(() => {
    if (isAutoPlay && currentSlide < slides.length - 1) {
      autoPlayRef.current = setTimeout(() => {
        nextSlide();
      }, SLIDE_DURATION);
    }
    
    return () => {
      if (autoPlayRef.current) {
        clearTimeout(autoPlayRef.current);
      }
    };
  }, [currentSlide, isAutoPlay, nextSlide, slides.length]);

  // Scroll to top quando cambia slide
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        setIsAutoPlay(false);
        nextSlide();
      } else if (e.key === "ArrowLeft") {
        setIsAutoPlay(false);
        prevSlide();
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, onClose]);

  // Group services by category
  const servicesByCategory = useMemo(() => {
    return preventivo.servizi.reduce((acc, service) => {
      if (!acc[service.categoria]) {
        acc[service.categoria] = [];
      }
      acc[service.categoria].push(service);
      return acc;
    }, {});
  }, [preventivo.servizi]);

  // URL pubblico per la condivisione (sempre /p/:share_token)
  const publicUrl = preventivo.share_token 
    ? `${window.location.origin}/p/${preventivo.share_token}`
    : window.location.href;

  const handleShare = (method) => {
    const url = publicUrl;
    const text = `Proposta di collaborazione 2026 - ${preventivo.cliente.ragione_sociale}`;
    
    if (method === 'whatsapp') {
      // wa.me è il dominio ufficiale WhatsApp per la condivisione
      window.location.href = `https://wa.me/?text=${encodeURIComponent(text + '\n\n' + url)}`;
    } else if (method === 'email') {
      // Crea un link temporaneo per forzare l'apertura del client email
      const a = document.createElement('a');
      a.href = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent('Buongiorno,\n\nle invio la nostra proposta di collaborazione. Può visualizzarla cliccando sul link:\n\n' + url + '\n\nCordiali saluti,\nSC Dottori Commercialisti SRL STP')}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (method === 'copy') {
      navigator.clipboard.writeText(url).then(() => {
        toast.success("Link copiato!");
      }).catch(() => {
        // Fallback
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        toast.success("Link copiato!");
      });
    }
  };

  const renderSlide = (slideOverride = null, keyOverride = null) => {
    const slide = slideOverride || slides[currentSlide];
    const key = keyOverride || slideKey;

    switch (slide.type) {
      case "copertina":
        return (
          <div key={key} className="flex flex-col items-center justify-center min-h-[85vh] text-center px-4">
            <div className="animate-slide-up max-w-4xl">
              {/* Badge anno */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-8">
                <Calendar className="w-4 h-4" />
                Proposta di Collaborazione 2026
              </div>
              
              {/* Titolo principale - più elegante */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 tracking-tight leading-tight">
                La tua azienda,<br/>
                <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                  sotto controllo
                </span>
              </h1>
              
              <p className="text-xl text-slate-500 mb-10 max-w-2xl mx-auto">
                Un sistema completo per gestire adempimenti, monitorare i numeri 
                e prendere decisioni strategiche con serenità.
              </p>
              
              {/* Card cliente */}
              <div className="mb-10">
                <div className="inline-block px-10 py-6 bg-white rounded-2xl shadow-lg border border-slate-200">
                  <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Preparato per</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
                    {preventivo.cliente.ragione_sociale}
                  </h2>
                  {preventivo.cliente.partita_iva && (
                    <p className="text-slate-500 text-sm mt-2">P.IVA {preventivo.cliente.partita_iva}</p>
                  )}
                </div>
              </div>
              
              {/* Footer studio */}
              <div className="flex items-center justify-center gap-3 text-slate-500">
                <Building2 className="w-5 h-5" />
                <span className="font-medium text-slate-700">SC Dottori Commercialisti SRL STP</span>
                <span className="text-slate-300">|</span>
                <span className="text-sm">Dottori Commercialisti e Revisori dei conti</span>
              </div>
            </div>
          </div>
        );

      case "trasformazione":
        return (
          <div key={key} className="max-w-6xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 text-center">
              Da dove parti... a dove arrivi
            </h2>
            <p className="text-xl text-slate-500 mb-10 text-center">
              Non e solo contabilita. E un <strong className="text-slate-800">cambio di paradigma</strong>.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* PRIMA - Situazione attuale */}
              <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-6 border-2 border-slate-300 relative">
                <div className="absolute -top-4 left-6 bg-slate-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  PRIMA
                </div>
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-slate-700 font-medium">Fai tutto da solo</p>
                      <p className="text-slate-500 text-sm">Decisioni al buio, senza dati affidabili</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-slate-700 font-medium">Paura delle tasse</p>
                      <p className="text-slate-500 text-sm">"Quanto dovro pagare?" scoperto a fine anno</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-slate-700 font-medium">Non sai come sta andando</p>
                      <p className="text-slate-500 text-sm">Bilanci vecchi di mesi, nessun KPI</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-slate-700 font-medium">Incombenze che ti schiacciano</p>
                      <p className="text-slate-500 text-sm">Scadenze, documenti, burocrazia</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* DOPO - Con me */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 rounded-2xl p-6 border-2 border-emerald-400 relative shadow-lg">
                <div className="absolute -top-4 left-6 bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  CON ME
                </div>
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold">Controllo totale</p>
                      <p className="text-slate-600 text-sm">Sai esattamente cosa fare, quando e perche</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold">Imposte previste e pianificate</p>
                      <p className="text-slate-600 text-sm">Simulazioni durante l'anno, scelte consapevoli</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold">Bilanci sempre aggiornati</p>
                      <p className="text-slate-600 text-sm">Margini, cassa, obiettivi - in tempo reale</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-semibold">Mai piu solo</p>
                      <p className="text-slate-600 text-sm">Uno studio che anticipa ogni tua necessita</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-500 to-violet-500 rounded-xl text-center">
              <p className="text-white text-xl font-semibold">
                <Sparkles className="w-6 h-6 inline mr-2" />
                Entri in un <strong>sistema che ti toglie le incombenze con serenita</strong> e ti da il controllo della tua azienda
              </p>
            </div>
          </div>
        );

      case "differenza":
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 text-center">
              Perché il nostro studio è diverso
            </h2>
            <p className="text-xl text-slate-500 mb-10 text-center">
              Molti studi offrono "tenuta contabilità + adempimenti".<br/>
              Noi ti offriamo qualcosa in più: <strong className="text-slate-800">strumenti per il controllo della tua azienda</strong>.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 hover:border-blue-400/50 transition-all">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Numeri leggibili</h3>
                <p className="text-slate-600 text-sm">KPI e margini, non solo registrazioni</p>
              </div>
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 hover:border-emerald-400/50 transition-all">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Decisioni guidate</h3>
                <p className="text-slate-600 text-sm">Simulazioni e scenari prima di scegliere</p>
              </div>
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 hover:border-violet-400/50 transition-all">
                <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Controllo continuo</h3>
                <p className="text-slate-600 text-sm">Obiettivi, scostamenti e azioni correttive</p>
              </div>
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 hover:border-amber-400/50 transition-all">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                  <FileCheck className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Tracciabilità</h3>
                <p className="text-slate-600 text-sm">Riunioni con verbale operativo (decisioni, responsabilità, scadenze)</p>
              </div>
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 hover:border-rose-400/50 transition-all">
                <div className="w-12 h-12 rounded-xl bg-rose-500/20 flex items-center justify-center mb-4">
                  <Calculator className="w-6 h-6 text-rose-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Pianificazione fiscale</h3>
                <p className="text-slate-600 text-sm">Imposte stimate in anticipo, scelte fatte per tempo</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-500/20 to-violet-500/20 backdrop-blur rounded-xl p-6 border border-blue-400/30">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <PlusCircle className="w-6 h-6 text-slate-800" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Pronti quando vuoi</h3>
                <p className="text-slate-600 text-sm">Tutti questi strumenti sono già disponibili. Potrai attivarli <strong>con un click</strong> quando ne avrai bisogno.</p>
              </div>
            </div>
          </div>
        );

      case "incluso":
        return (
          <div key={key} className="max-w-6xl mx-auto py-8 px-4">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-10 text-center animate-slide-up">
              Cosa includo <span className="text-slate-500">(in modo chiaro)</span>
            </h2>
            
            <div className={`grid gap-6 ${serviziStrategici.length > 0 ? 'md:grid-cols-2' : 'max-w-2xl mx-auto'}`}>
              {/* Area Amministrativa */}
              <div className="bg-gradient-to-br from-blue-50 to-sky-100 backdrop-blur rounded-2xl p-6 border border-blue-200 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-slate-800" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">Area Amministrativa e Fiscale</h3>
                    <p className="text-blue-300 text-sm">(obblighi)</p>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {serviziAmministrativi.length > 0 ? (
                    serviziAmministrativi.map((s, i) => (
                      <li 
                        key={i} 
                        className="flex items-start gap-3 opacity-0 animate-fade-in-up"
                        style={{ animationDelay: `${0.5 + i * 0.15}s`, animationFillMode: 'forwards' }}
                      >
                        <Check className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{s.nome}</span>
                      </li>
                    ))
                  ) : (
                    <>
                      {['Tenuta contabilita ordinaria', 'Liquidazioni IVA mensili', 'LIPE trimestrali', 'Bilancio e deposito', 'Dichiarazioni (IRES/IRAP/IVA)', 'Assistenza ordinaria su quesiti'].map((item, i) => (
                        <li 
                          key={i}
                          className="flex items-start gap-3 opacity-0 animate-fade-in-up"
                          style={{ animationDelay: `${0.5 + i * 0.15}s`, animationFillMode: 'forwards' }}
                        >
                          <Check className="w-5 h-5 text-blue-400 mt-0.5" />
                          <span className="text-slate-600">{item}</span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
              
              {/* Area Strategica - Solo se ci sono servizi di consulenza */}
              {serviziStrategici.length > 0 ? (
                <div className="bg-gradient-to-br from-violet-50 to-purple-100 backdrop-blur rounded-2xl p-6 border border-violet-200 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-violet-500 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">Area Strategica</h3>
                      <p className="text-violet-600 text-sm">(valore)</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-3">
                    {serviziStrategici.map((s, i) => (
                      <li 
                        key={i} 
                        className="flex items-start gap-3 opacity-0 animate-fade-in-up"
                        style={{ animationDelay: `${0.7 + i * 0.15}s`, animationFillMode: 'forwards' }}
                      >
                        <Sparkles className="w-5 h-5 text-violet-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{s.nome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                /* Nota attivabile quando NON ci sono servizi strategici */
                <div className="bg-gradient-to-br from-amber-50 to-orange-100 backdrop-blur rounded-2xl p-6 border border-amber-300 border-dashed animate-slide-up" style={{ animationDelay: '0.4s' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-400 flex items-center justify-center">
                      <PlusCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">Pannello di Controllo Direzionale</h3>
                      <p className="text-amber-600 text-sm font-medium">Attivabile in qualsiasi momento</p>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 text-sm mb-4">
                    Se vorrai il <strong>controllo totale</strong> sulla tua azienda, potrai attivare:
                  </p>
                  
                  <ul className="space-y-2">
                    {['Consulenza Strategica Trimestrale', 'Canone Piattaforma Gestionale', 'KPI, simulazioni e piano fiscale'].map((item, i) => (
                      <li 
                        key={i}
                        className="flex items-start gap-2 text-slate-500 text-sm opacity-0 animate-fade-in-up"
                        style={{ animationDelay: `${0.7 + i * 0.2}s`, animationFillMode: 'forwards' }}
                      >
                        <ArrowRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <p className="text-amber-700 text-xs mt-4 font-medium opacity-0 animate-fade-in-up" style={{ animationDelay: '1.5s', animationFillMode: 'forwards' }}>
                    Ne parliamo piu avanti nella presentazione!
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case "prezzo":
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 text-center">
              Come funziona il prezzo
            </h2>
            <p className="text-xl text-slate-500 mb-10 text-center">(trasparenza totale)</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Canone contabilita */}
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-slate-800 font-bold">1</div>
                  <h3 className="text-xl font-bold text-slate-800">Canone contabilita con "soglia inclusa"</h3>
                </div>
                
                <div className="space-y-4 text-slate-600">
                  <p>Il canone base include fino a <strong className="text-slate-800">600 registrazioni/anno</strong></p>
                  <p className="text-sm">(monitoraggio: 150 registrazioni a trimestre)</p>
                  <p>Ogni trimestre verifichiamo il volume effettivo.</p>
                  <p>Se il volume supera la soglia, <strong className="text-slate-800">adeguiamo il canone allo scaglione corretto</strong> (senza sorprese a fine anno).</p>
                </div>
                
                <div className="mt-4 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                  <p className="text-blue-300 text-sm">
                    <Lightbulb className="w-4 h-4 inline mr-1" />
                    Perche cosi: paghi in modo proporzionale al lavoro reale e io posso garantire qualita e tempi.
                  </p>
                </div>
              </div>
              
              {/* Canone strategico */}
              <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 backdrop-blur rounded-2xl p-6 border border-violet-500/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center text-slate-800 font-bold">2</div>
                  <h3 className="text-xl font-bold text-slate-800">Canone "Strategico"</h3>
                </div>
                
                <p className="text-violet-300 text-sm mb-4">(piattaforma + governance)</p>
                
                <div className="space-y-4 text-slate-600">
                  <p>Qui non paghi "tempo", paghi <strong className="text-slate-800">risultati e metodo</strong>:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-violet-400" />
                      <span>Cruscotto</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-violet-400" />
                      <span>Simulazioni</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-violet-400" />
                      <span>Riunioni trimestrali</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-violet-400" />
                      <span>Verbali operativi</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-violet-400" />
                      <span>Obiettivi e scostamenti</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case "piattaforma":
      case "pannello_controllo":
        return (
          <div key={key} className="max-w-6xl mx-auto py-6 px-4 animate-slide-up">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-full text-sm font-bold mb-4">
                <Sparkles className="w-4 h-4" />
                IL CUORE DEL SISTEMA
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-2">
                Pannello di Controllo Direzionale
              </h2>
              <p className="text-xl text-slate-500">
                Lo strumento che ti da il <strong className="text-slate-800">controllo totale</strong> della tua azienda
              </p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-4 mb-6">
              {/* Colonna 1: Sapere */}
              <div className="bg-gradient-to-br from-blue-50 to-sky-100 rounded-2xl p-5 border border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">VEDERE</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">Sai esattamente come sta andando:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-blue-500" />
                    <span>Margini in tempo reale</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-blue-500" />
                    <span>Cassa e flussi previsionali</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-blue-500" />
                    <span>KPI chiave del tuo settore</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-blue-500" />
                    <span>Scostamenti vs obiettivi</span>
                  </li>
                </ul>
              </div>
              
              {/* Colonna 2: Decidere */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 rounded-2xl p-5 border border-emerald-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">DECIDERE</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">Sai esattamente cosa fare:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Simulazioni "what-if"</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Scenari alternativi</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Piano fiscale dinamico</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Azioni prioritizzate</span>
                  </li>
                </ul>
              </div>
              
              {/* Colonna 3: Eseguire */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-100 rounded-2xl p-5 border border-violet-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">ESEGUIRE</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">Trasmetti e misuri tutto il team:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-violet-500" />
                    <span>Verbali con responsabilita</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-violet-500" />
                    <span>Scadenze tracciate</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-violet-500" />
                    <span>Obiettivi condivisi</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-violet-500" />
                    <span>Correzioni tempestive</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Consulenza Strategica */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-100 rounded-2xl p-5 border border-amber-300 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-800 mb-2">Consulenza Strategica Trimestrale</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    Ogni trimestre ci incontriamo per analizzare i numeri, definire obiettivi e creare il piano d'azione. 
                    Il verbale diventa la tua roadmap operativa con responsabilita, scadenze e metriche di successo.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-amber-200/50 text-amber-800 rounded-full text-xs font-medium">Riunione 90-120 min</span>
                    <span className="px-3 py-1 bg-amber-200/50 text-amber-800 rounded-full text-xs font-medium">Verbale operativo</span>
                    <span className="px-3 py-1 bg-amber-200/50 text-amber-800 rounded-full text-xs font-medium">Piano fiscale aggiornato</span>
                    <span className="px-3 py-1 bg-amber-200/50 text-amber-800 rounded-full text-xs font-medium">Obiettivi misurabili</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Call to action */}
            <div className="p-5 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl text-center">
              <p className="text-white text-lg font-semibold mb-2">
                <Zap className="w-5 h-5 inline mr-2 text-amber-400" />
                Con il Pannello di Controllo Direzionale passi da "sperare che vada bene" a <strong className="text-amber-400">sapere esattamente come sta andando</strong>
              </p>
              <p className="text-slate-400 text-sm">
                E hai uno studio che ti anticipa ogni necessita, non che ti rincorre con le scadenze
              </p>
            </div>
          </div>
        );

      case "confronto":
      case "confronto_noi":
        return (
          <div key={key} className="max-w-6xl mx-auto py-6 px-4 animate-slide-up">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
                Non tutti i commercialisti sono uguali
              </h2>
              <p className="text-lg text-slate-500">Ecco cosa cambia quando scegli noi</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-5">
              {/* Colonna LORO */}
              <div className="bg-slate-100 rounded-2xl p-5 border border-slate-200">
                <div className="flex items-center gap-3 mb-5 pb-3 border-b border-slate-200">
                  <div className="w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-500 text-sm uppercase tracking-wider">Fiscalisti generici</p>
                    <p className="text-xs text-slate-400">Quello che trovi la fuori</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    "Ti assegnano alla prima persona disponibile, che cambia di continuo",
                    "Il tuo bilancio lo vede il commercialista solo per firmarlo",
                    "Ti chiamano solo per le scadenze",
                    "I numeri li vedi una volta l'anno (quando e tardi)",
                    "Per qualsiasi domanda aspetti giorni",
                    "Se hai bisogno di altro, ricomincia da capo con un altro studio",
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-slate-500 text-sm">{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Colonna NOI */}
              <div className="bg-gradient-to-br from-blue-50 to-sky-100 rounded-2xl p-5 border-2 border-blue-400 shadow-lg relative">
                <div className="absolute -top-3 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                  SC Dottori Commercialisti
                </div>
                <div className="flex items-center gap-3 mb-5 pb-3 border-b border-blue-200">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-blue-800 text-sm uppercase tracking-wider">Con noi</p>
                    <p className="text-xs text-blue-500">Anche con il pacchetto base</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { text: "Un Dottore Commercialista dedicato, sempre lo stesso, che conosce la tua azienda", bold: true },
                    { text: "Il tuo bilancio lo costruiamo insieme, trimestre dopo trimestre" },
                    { text: "Consulenza periodica per analizzare i numeri e prevedere il reddito" },
                    { text: "Numeri sempre aggiornati, non sorprese a fine anno" },
                    { text: "Risposte rapide, perche chi ti segue sa gia tutto di te" },
                    { text: "Puoi aggiungere servizi in qualsiasi momento: contenzioso, bandi, consulenza avanzata" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p className={`text-sm ${item.bold ? 'text-blue-900 font-semibold' : 'text-slate-700'}`}>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "pacchetti":
        return (
          <div key={key} className="max-w-6xl mx-auto py-4 px-4 animate-slide-up">
            <div className="text-center mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
                Scegli il livello di controllo che vuoi
              </h2>
              <p className="text-base text-slate-500">Abbiamo pensato a 3 livelli per ogni esigenza</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {/* Pacchetto 1: Azienda in Regola */}
              <div className={`rounded-2xl p-5 border-2 transition-all ${
                !hasConsulenza && !hasPannelloControllo 
                  ? 'border-blue-500 bg-blue-50 shadow-xl scale-[1.02]' 
                  : 'border-slate-200 bg-white'
              }`}>
                {!hasConsulenza && !hasPannelloControllo && (
                  <div className="text-center mb-3">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      Il tuo piano
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="w-12 h-12 mx-auto bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                    <Shield className="w-6 h-6 text-slate-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Azienda in Regola</h3>
                  <p className="text-xs text-slate-500 mt-1">Tutti gli adempimenti, zero pensieri</p>
                </div>
                <div className="space-y-2 mb-4">
                  {[
                    "Contabilita e registrazioni",
                    "Dichiarazioni fiscali",
                    "Bilancio e deposito",
                    "Adempimenti IVA",
                    "770 e Certificazioni Uniche",
                    "Commercialista dedicato",
                    "Risposte rapide",
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      <span className="text-xs text-slate-600">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pacchetto 2: Consulenza Strategica */}
              <div className={`rounded-2xl p-5 border-2 transition-all ${
                hasConsulenza && !hasPannelloControllo 
                  ? 'border-blue-500 bg-blue-50 shadow-xl scale-[1.02]' 
                  : 'border-slate-200 bg-white'
              }`}>
                {hasConsulenza && !hasPannelloControllo && (
                  <div className="text-center mb-3">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      Il tuo piano
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="w-12 h-12 mx-auto bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                    <Target className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Consulenza Strategica</h3>
                  <p className="text-xs text-slate-500 mt-1">Adempimenti + consulenza attiva</p>
                </div>
                <div className="space-y-2 mb-4">
                  {[
                    { text: "Tutto di Azienda in Regola", muted: true },
                    "Consulenza periodica sui numeri",
                    "Previsione reddito e imposte",
                    "Pianificazione fiscale proattiva",
                    "Analisi marginalita e costi",
                    "Riunione trimestrale strategica",
                    "Simulazioni e scenari",
                  ].map((f, i) => {
                    const text = typeof f === 'string' ? f : f.text;
                    const muted = typeof f === 'object' && f.muted;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Check className={`w-3.5 h-3.5 flex-shrink-0 ${muted ? 'text-slate-300' : 'text-emerald-500'}`} />
                        <span className={`text-xs ${muted ? 'text-slate-400 italic' : 'text-slate-600 font-medium'}`}>{text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pacchetto 3: Software Pannello di Controllo Direzionale */}
              <div className={`rounded-2xl p-5 border-2 transition-all relative ${
                hasPannelloControllo 
                  ? 'border-blue-500 bg-blue-50 shadow-xl scale-[1.02]' 
                  : 'border-amber-300 bg-gradient-to-b from-amber-50 to-white'
              }`}>
                {hasPannelloControllo ? (
                  <div className="text-center mb-3">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      Il tuo piano
                    </span>
                  </div>
                ) : (
                  <div className="text-center mb-3">
                    <span className="px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1 w-fit mx-auto">
                      <Sparkles className="w-3 h-3" /> Consigliato
                    </span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="w-12 h-12 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-3">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Software Pannello di Controllo Direzionale</h3>
                  <p className="text-xs text-slate-500 mt-1">Il copilota per le tue decisioni</p>
                </div>
                <div className="space-y-2 mb-4">
                  {[
                    { text: "Tutto della Consulenza Strategica", muted: true },
                    "Pannello di Controllo Direzionale",
                    "Semafori: verde/giallo/rosso istantanei",
                    "Obiettivi a cascata: anno > mese > settimana > oggi",
                    "Riunione strategica con script pronti",
                    "Previsione imposte automatica",
                    "Analisi esperienza cliente",
                  ].map((f, i) => {
                    const text = typeof f === 'string' ? f : f.text;
                    const muted = typeof f === 'object' && f.muted;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Check className={`w-3.5 h-3.5 flex-shrink-0 ${muted ? 'text-slate-300' : 'text-amber-500'}`} />
                        <span className={`text-xs ${muted ? 'text-slate-400 italic' : 'text-slate-700 font-medium'}`}>{text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case "proposta_canone":
        const serviziM = preventivo.servizi.filter(s => s.billing_type === 'monthly');
        const serviziO = preventivo.servizi.filter(s => s.billing_type === 'one_off');
        const subM = preventivo.subtotale_monthly || serviziM.reduce((acc, s) => acc + s.prezzo_totale, 0);
        const subO = preventivo.subtotale_one_off || serviziO.reduce((acc, s) => acc + s.prezzo_totale, 0);
        const scontoPctCanone = preventivo.sconto_percentuale || 0;
        const scontoMVal = subM * (scontoPctCanone / 100);
        const scontoOVal = subO * (scontoPctCanone / 100);
        const canoneCalc = (subM - scontoMVal) / 12;
        const nettoOneOffCalc = subO - scontoOVal;
        
        return (
          <div key={key} className="max-w-5xl mx-auto py-6 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Il tuo investimento
            </h2>
            <p className="text-base text-slate-500 mb-6 text-center">per {preventivo.cliente.ragione_sociale}</p>
            
            {/* CANONE MENSILE - Grande, prominente */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-6 md:p-8 text-white shadow-2xl mb-5 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent)]"></div>
              <div className="relative">
                {preventivo.sconto_percentuale > 0 && (
                  <div className="text-center mb-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-400 text-emerald-900 rounded-full text-xs font-bold">
                      <Star className="w-3 h-3" /> Sconto {preventivo.sconto_percentuale}% applicato
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-5">
                  <p className="text-blue-200 text-sm font-semibold uppercase tracking-widest mb-3">Canone mensile tutto incluso</p>
                  <p className="text-5xl md:text-7xl font-black tracking-tight">
                    {formatCurrency(canoneCalc)}
                  </p>
                  <p className="text-blue-200 text-sm mt-2">+IVA 22% | +Cassa Prev. 4%</p>
                </div>
                
                {/* Elenco servizi inclusi nel canone */}
                <div className="bg-white/10 backdrop-blur rounded-2xl p-4 mb-4">
                  <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold mb-3 text-center">
                    Cosa include il tuo canone mensile
                  </p>
                  <div className={`grid gap-2 text-left ${serviziM.length > 6 ? 'grid-cols-2' : 'max-w-md mx-auto'}`}>
                    {serviziM.map((s, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-white">
                        <Check className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
                        <span>{s.nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Pagamento */}
                <div className="pt-3 border-t border-blue-400/30 text-center">
                  <p className="text-blue-100 text-sm">
                    <CreditCard className="w-4 h-4 inline mr-2" />
                    Pagamento entro il <strong className="text-white">{preventivo.giorno_incasso || 10} del mese</strong> tramite bonifico o RID/SEPA
                  </p>
                </div>
              </div>
            </div>
            
            {/* SERVIZI UNA TANTUM - Elenco completo */}
            {serviziO.length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-3">
                  Servizi una tantum (fatturati all'erogazione)
                </p>
                <div className="space-y-2">
                  {serviziO.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-700">{s.nome}</span>
                      <span className="text-sm font-semibold text-slate-800 font-mono ml-4 whitespace-nowrap">{formatCurrency(s.prezzo_totale)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-200 px-3">
                    <span className="text-sm font-semibold text-slate-600">Totale una tantum</span>
                    <span className="text-sm font-bold text-slate-800 font-mono">{formatCurrency(nettoOneOffCalc)} +IVA +Cassa Prev.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "upgrade":
        return (
          <div key={key} className="max-w-6xl mx-auto py-4 px-4 animate-slide-up">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-sm font-bold mb-3">
                <Sparkles className="w-4 h-4" />
                UPGRADE DISPONIBILI
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
                Vuoi di piu dalla tua azienda?
              </h2>
              <p className="text-base text-slate-500">Puoi attivare un upgrade in qualsiasi momento</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-5">
              {/* Upgrade 1: Consulenza Strategica */}
              <div className="bg-white rounded-2xl p-6 border-2 border-amber-200 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-orange-400"></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Target className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">Consulenza Strategica</h3>
                    <p className="text-xs text-slate-500">Il tuo partner per crescere</p>
                  </div>
                </div>
                <div className="space-y-2.5 mb-5">
                  {[
                    "Scopri i margini di ogni servizio o prodotto che vendi",
                    "Crea offerte e abbinamenti personalizzati per aumentare i margini",
                    "Scopri chi è il tuo cliente target e come trovarne altri",
                    "Implementa strategie di sviluppo e crescita sostenibile",
                    "Pianificazione fiscale proattiva: sai gia a luglio quanto pagherai a novembre",
                    "Simulazioni: \"se compro questo macchinario, quanto risparmio di tasse?\"",
                    "Riunioni trimestrali per analizzare i numeri e definire le azioni",
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-600">{text}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-amber-600 font-medium">Attivabile in qualsiasi momento</p>
                </div>
              </div>

              {/* Upgrade 2: Software Pannello di Controllo Direzionale */}
              <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-6 border-2 border-amber-400 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
                <div className="absolute -top-1 right-4 px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-b-lg">
                  TOP
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Software Pannello di Controllo Direzionale</h3>
                    <p className="text-xs text-slate-400">Il cruscotto per decidere con i numeri</p>
                  </div>
                </div>
                <div className="space-y-2.5 mb-5">
                  {[
                    "Tutto della Consulenza Strategica, piu...",
                    "Ogni giorno sai quanto ti manca per centrare il tuo obiettivo giornaliero, settimanale, mensile e annuale",
                    "Pannello con semafori: verde/giallo/rosso su ogni area aziendale",
                    "Obiettivi a cascata: dall'anno al giorno, sai sempre cosa fare",
                    "Riunione strategica con script gia pronti per il tuo team",
                    "Previsione imposte che si aggiorna in automatico",
                    "\"Non hai mai visto niente di simile\" - nato da 20 anni di riunioni con imprenditori",
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-300">{text}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-500/20 rounded-xl p-3 text-center border border-amber-500/30">
                  <p className="text-xs text-amber-300 font-medium">Il passo definitivo verso il controllo totale della tua azienda</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "proposta":
        // Separa servizi per billing_type
        const serviziMonthly = preventivo.servizi.filter(s => s.billing_type === 'monthly');
        const serviziOneOff = preventivo.servizi.filter(s => s.billing_type === 'one_off');
        const subtotaleMonthlyProp = preventivo.subtotale_monthly || serviziMonthly.reduce((acc, s) => acc + s.prezzo_totale, 0);
        const subtotaleOneOffProp = preventivo.subtotale_one_off || serviziOneOff.reduce((acc, s) => acc + s.prezzo_totale, 0);
        const scontoPctProp = preventivo.sconto_percentuale || 0;
        const nettoOneOffProp = subtotaleOneOffProp - (subtotaleOneOffProp * (scontoPctProp / 100));
        const hasMonthly = serviziMonthly.length > 0;
        const hasOneOff = serviziOneOff.length > 0;
        
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 text-center animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4">
              Proposta Economica
            </h2>
            <p className="text-xl text-slate-500 mb-8">per {preventivo.cliente.ragione_sociale}</p>
            
            <div className={`grid gap-6 ${hasMonthly && hasOneOff ? 'md:grid-cols-2' : 'max-w-lg mx-auto'}`}>
              {/* Canone Mensile - solo se ci sono servizi ricorrenti */}
              {hasMonthly && (
                <div className="bg-gradient-to-br from-blue-50 to-sky-100 backdrop-blur rounded-3xl p-6 border border-blue-200 shadow-xl">
                  {preventivo.sconto_percentuale > 0 && (
                    <div className="mb-4">
                      <span className="inline-flex items-center gap-2 px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold border border-emerald-300">
                        <Star className="w-3 h-3" />
                        Sconto {preventivo.sconto_percentuale}%
                      </span>
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <p className="text-blue-600 text-sm font-semibold mb-2 uppercase tracking-wider">Canone Mensile</p>
                    <p className="text-4xl md:text-5xl font-extrabold text-slate-800 font-mono tracking-tight">
                      {formatCurrency(nettoMensile)}
                    </p>
                    <p className="text-slate-500 mt-2 text-sm">+ Cassa prev. 4% + IVA 22%</p>
                  </div>
                  
                  {/* Lista servizi ricorrenti */}
                  <div className="text-left border-t border-blue-200 pt-4">
                    <p className="text-blue-600 text-xs mb-3 uppercase tracking-wider font-semibold">Servizi ricorrenti inclusi</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {serviziMonthly.map((s, i) => (
                        <div key={i} className="flex justify-between items-center text-xs">
                          <span className="text-slate-700 truncate pr-2">{s.nome}</span>
                          <span className="text-slate-800 font-mono flex-shrink-0">{formatCurrency(s.prezzo_totale)}/anno</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Servizi Una Tantum - solo se ci sono */}
              {hasOneOff && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-100 backdrop-blur rounded-3xl p-6 border border-amber-200 shadow-xl">
                  <div className="mb-6">
                    <p className="text-amber-600 text-sm font-semibold mb-2 uppercase tracking-wider">
                      {hasMonthly ? "Servizi da fatturare all'erogazione" : "Totale Pratiche"}
                    </p>
                    <p className="text-4xl md:text-5xl font-extrabold text-slate-800 font-mono tracking-tight">
                      {formatCurrency(nettoOneOffProp)}
                    </p>
                    <p className="text-slate-500 mt-2 text-sm">+ Cassa prev. 4% + IVA 22%</p>
                  </div>
                  
                  {/* Lista servizi una tantum */}
                  <div className="text-left border-t border-amber-200 pt-4">
                    <p className="text-amber-600 text-xs mb-3 uppercase tracking-wider font-semibold">Dettaglio servizi</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {serviziOneOff.map((s, i) => (
                        <div key={i} className="flex justify-between items-center text-xs">
                          <span className="text-slate-700 truncate pr-2">{s.nome}</span>
                          <span className="text-slate-800 font-mono flex-shrink-0">{formatCurrency(s.prezzo_totale)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Totale complessivo - RIMOSSO: canone e una tantum non vanno sommati */}
          </div>
        );

      case "upgrade_pannello":
        return (
          <div key={key} className="max-w-6xl mx-auto py-6 px-4 animate-slide-up">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-sm font-bold mb-4">
                <Sparkles className="w-4 h-4" />
                IL PASSO IN PIU
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-2">
                Vuoi il controllo totale?
              </h2>
              <p className="text-xl text-slate-500">
                Attiva il <strong className="text-slate-800">Pannello di Controllo Direzionale</strong> in qualsiasi momento
              </p>
            </div>
            
            {/* Confronto SENZA vs CON */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Senza Pannello */}
              <div className="bg-slate-100 rounded-2xl p-5 border border-slate-300">
                <div className="text-center mb-4">
                  <p className="text-slate-500 text-sm uppercase tracking-wider font-medium">Oggi hai scelto</p>
                  <p className="text-slate-800 text-lg font-bold">Contabilita + Adempimenti</p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <span>Registrazioni contabili precise</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <span>Dichiarazioni in regola</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-emerald-500" />
                    <span>Scadenze rispettate</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-400 text-sm">
                    <X className="w-5 h-5 text-slate-300" />
                    <span>KPI e margini in tempo reale</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-400 text-sm">
                    <X className="w-5 h-5 text-slate-300" />
                    <span>Simulazioni e scenari</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-400 text-sm">
                    <X className="w-5 h-5 text-slate-300" />
                    <span>Riunioni strategiche trimestrali</span>
                  </li>
                </ul>
              </div>
              
              {/* Con Pannello */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-100 rounded-2xl p-5 border-2 border-violet-400 shadow-lg relative">
                <div className="absolute -top-3 right-4 bg-violet-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  CONSIGLIATO
                </div>
                <div className="text-center mb-4">
                  <p className="text-violet-600 text-sm uppercase tracking-wider font-medium">Puoi attivare</p>
                  <p className="text-slate-800 text-lg font-bold">Pannello di Controllo Direzionale</p>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-700 text-sm">
                    <Check className="w-5 h-5 text-violet-500" />
                    <span><strong>Tutto cio che hai gia</strong> +</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 text-sm">
                    <Check className="w-5 h-5 text-violet-500" />
                    <span>Cruscotto con margini e cassa</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 text-sm">
                    <Check className="w-5 h-5 text-violet-500" />
                    <span>Simulazioni fiscali dinamiche</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 text-sm">
                    <Check className="w-5 h-5 text-violet-500" />
                    <span>Riunione trimestrale con verbale</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 text-sm">
                    <Check className="w-5 h-5 text-violet-500" />
                    <span>Obiettivi misurabili per il team</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-700 text-sm">
                    <Check className="w-5 h-5 text-violet-500" />
                    <span>Correzioni tempestive</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Messaggio chiave */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-center mb-4">
              <p className="text-white text-lg font-semibold mb-2">
                <Zap className="w-5 h-5 inline mr-2 text-amber-400" />
                Con il Pannello passi da <span className="text-red-300">"dover fare tutto da solo"</span> a <span className="text-emerald-400">"avere il controllo totale"</span>
              </p>
              <p className="text-slate-400 text-sm">
                Bilanci sempre aggiornati, imposte previste, e uno studio che anticipa ogni tua necessita
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-slate-600">
                <Lightbulb className="w-5 h-5 inline mr-2 text-amber-500" />
                Puoi attivarlo <strong>in qualsiasi momento</strong> - basta chiedere e lo aggiungiamo al canone
              </p>
            </div>
          </div>
        );

      case "servizi_extra":
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 text-center">
              Servizi strategici attivabili
            </h2>
            <p className="text-xl text-slate-500 mb-8 text-center">
              Puoi attivare questi servizi in qualsiasi momento per potenziare la collaborazione
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {serviziStrategiciNonInclusi.length > 0 ? (
                serviziStrategiciNonInclusi.map((servizio, i) => (
                  <div 
                    key={i}
                    className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 backdrop-blur rounded-xl p-5 border border-violet-400/30 hover:border-violet-400/50 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                        <PlusCircle className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <h3 className="text-slate-800 font-semibold mb-1">{servizio.nome}</h3>
                        <p className="text-slate-600 text-sm">{servizio.descrizione}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-400" />
                  </div>
                  <p className="text-slate-800 text-lg font-medium">Hai gia incluso tutti i servizi strategici!</p>
                  <p className="text-slate-500 mt-2">Il tuo preventivo e completo di tutte le opzioni disponibili.</p>
                </div>
              )}
            </div>
            
            {serviziStrategiciNonInclusi.length > 0 && (
              <div className="mt-8 p-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl border border-amber-400/30 text-center">
                <p className="text-amber-200 text-lg">
                  <Lightbulb className="w-5 h-5 inline mr-2" />
                  Questi servizi possono essere aggiunti <strong className="text-slate-800">in qualsiasi momento</strong> con un semplice adeguamento del canone
                </p>
              </div>
            )}
          </div>
        );

      case "garanzie":
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 text-center">
              Garanzie di metodo
            </h2>
            <p className="text-xl text-slate-500 mb-10 text-center">(quello che ti fa dire "ok")</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Mostra Agenda/Incontro trimestrale solo se ha consulenza o pannello */}
              {(hasConsulenza || hasPannelloControllo) && (
                <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Agenda</h3>
                    <p className="text-slate-600">Incontro trimestrale già pianificato (scadenze fisse)</p>
                  </div>
                </div>
              )}
              
              {/* Mostra Output solo se ha consulenza o pannello */}
              {(hasConsulenza || hasPannelloControllo) && (
                <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Output</h3>
                    <p className="text-slate-600">Cruscotto + verbale operativo (cosa fare, chi, quando)</p>
                  </div>
                </div>
              )}
              
              {/* Mostra Misurazione solo se ha consulenza o pannello */}
              {(hasConsulenza || hasPannelloControllo) && (
                <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Misurazione</h3>
                    <p className="text-slate-600">Obiettivi e scostamenti (non "sensazioni")</p>
                  </div>
                </div>
              )}
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">Controllo costi</h3>
                  <p className="text-slate-600">Canone + regole di adeguamento (trasparenti)</p>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur rounded-xl p-6 border border-slate-200 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">Comunicazione</h3>
                  <p className="text-slate-600">Risposte rapide via email e WhatsApp</p>
                </div>
              </div>
              
              {/* Per chi non ha consulenza/pannello: mostra che può attivarli */}
              {!hasConsulenza && !hasPannelloControllo && (
                <div className="bg-gradient-to-br from-blue-500/20 to-violet-500/20 backdrop-blur rounded-xl p-6 border border-blue-400/30 flex items-start gap-4 md:col-span-2">
                  <div className="w-12 h-12 rounded-xl bg-white/30 flex items-center justify-center flex-shrink-0">
                    <PlusCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Pronti per crescere insieme</h3>
                    <p className="text-slate-600">Il nostro studio dispone di tutti gli strumenti per il controllo di gestione avanzato: riunioni trimestrali, cruscotto KPI, verbali operativi. <strong>Potrai attivarli in qualsiasi momento</strong> quando la tua azienda ne avrà bisogno.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "modalita_documenti":
        return (
          <div key={key} className="max-w-5xl mx-auto py-6 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Come semplifichiamo la gestione documentale
            </h2>
            <p className="text-lg text-slate-500 mb-6 text-center">Strumenti digitali per ridurre al minimo il tuo impegno</p>
            
            <div className="space-y-3">
              <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-slate-800 font-semibold mb-1">Fatture e Corrispettivi - Automatici</h3>
                    <p className="text-slate-600 text-sm">Con una semplice delega SPID, riceviamo automaticamente tutte le fatture elettroniche. <strong>Non dovrai più inviarci nulla manualmente.</strong></p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-slate-800 font-semibold mb-1">Movimenti Bancari - Automatici</h3>
                    <p className="text-slate-600 text-sm">Collegando la banca tramite <strong>Sportello Cloud</strong>, i movimenti arrivano in automatico. Ti ricorderemo noi quando rinnovare il consenso (ogni 6 mesi).</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">3</div>
                  <div>
                    <h3 className="text-slate-800 font-semibold mb-1">Altri documenti</h3>
                    <p className="text-slate-600 text-sm">Per tutto il resto (fatture estere, note spese, ecc.), basta caricarli su <strong>app.casalisilvia.com</strong> entro il {preventivo.giorno_incasso || 10} del mese.</p>
                  </div>
                </div>
              </div>
              
              {(hasConsulenza || hasPannelloControllo) && (
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 backdrop-blur rounded-xl p-4 border border-blue-200">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 text-white">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-slate-800 font-semibold mb-1">Riunione trimestrale</h3>
                      <p className="text-slate-600 text-sm">Non appena la documentazione del trimestre sarà completa, <strong>fisseremo subito la riunione</strong> per analizzare insieme i risultati.</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-slate-800 font-semibold mb-1">F24 - Ci pensiamo noi</h3>
                    <p className="text-slate-600 text-sm">Gli F24 delle scadenze fiscali vengono <strong>preparati e inviati direttamente dallo studio</strong>.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "onboarding":
        // Onboarding diverso in base a consulenza/pannello
        const onboardingSteps = (hasConsulenza || hasPannelloControllo) ? [
          { step: 1, title: "Raccolta dati e accessi", desc: "Contabilità/banche/gestionali se presenti" },
          { step: 2, title: "Set KPI e struttura cruscotto", desc: "Definiamo insieme gli indicatori chiave" },
          { step: 3, title: "Prima riunione", desc: "Obiettivi e priorità" },
          { step: 4, title: "Routine", desc: "Analisi trimestrale + verbale + aggiornamento piano fiscale" }
        ] : [
          { step: 1, title: "Raccolta dati e accessi", desc: "Contabilità/banche/gestionali se presenti" },
          { step: 2, title: "Attivazione deleghe", desc: "SPID per fatture elettroniche, collegamento banche" },
          { step: 3, title: "Inizio operatività", desc: "Registrazioni contabili e adempimenti" }
        ];
        
        return (
          <div key={key} className="max-w-4xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 text-center">
              Come partiamo
            </h2>
            <p className="text-xl text-slate-500 mb-10 text-center">(onboarding semplice)</p>
            
            <div className="space-y-4">
              {onboardingSteps.map((item, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-6 p-6 bg-white/80 backdrop-blur rounded-xl border border-slate-200 hover:border-blue-400/30 transition-all"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-slate-800 text-2xl font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{item.title}</h3>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "chisiamo":
        return (
          <div key={key} className="max-w-6xl mx-auto py-8 px-4 animate-slide-up">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4">
                Chi siamo
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Uno studio che va oltre la contabilita: un partner strategico per far crescere il tuo business
              </p>
            </div>
            
            {/* Punti di forza */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {PUNTI_FORZA_STUDIO.map((punto, i) => (
                <div key={i} className="bg-white/80 backdrop-blur rounded-xl p-5 border border-slate-200 hover:border-blue-400/30 transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-slate-800" />
                    </div>
                    <div>
                      <h3 className="text-slate-800 font-semibold mb-1">{punto.titolo}</h3>
                      <p className="text-slate-600 text-sm">{punto.descrizione}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Info Studio e Sedi */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-slate-100 to-slate-200 backdrop-blur rounded-xl p-6 border border-slate-300">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  {STUDIO_INFO.nome}
                </h3>
                
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-slate-500">{STUDIO_INFO.formaGiuridica}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Sede legale</p>
                    <p className="text-slate-800">{STUDIO_INFO.sedeLegale}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Sala riunioni</p>
                    <p className="text-slate-800">{STUDIO_INFO.salaRiunioni}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Sedi amministrative</p>
                    <p className="text-slate-800">{STUDIO_INFO.sediAmministrative.join(" • ")}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">C.F. e P.IVA</p>
                    <p className="text-slate-800">{STUDIO_INFO.piva} - SDI: {STUDIO_INFO.sdi}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Amministratore Unico</p>
                    <p className="text-slate-800">{STUDIO_INFO.amministratore}</p>
                    <p className="text-slate-600 text-xs">{STUDIO_INFO.albo}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-500/20 to-violet-500/20 rounded-xl p-6 border border-blue-400/30 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-blue-400" />
                  <h3 className="text-lg font-bold text-slate-800">Rete di Partner</h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  Abbiamo costruito una rete di professionisti selezionati per seguire ogni cliente a 360 gradi:
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Avvocati", "Notai", "Consulenti del Lavoro", "Agenzie Marketing", "Consulenti IT", "Periti"].map((partner, i) => (
                    <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-slate-600 text-xs">
                      {partner}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "recensioni":
        return (
          <div key={key} className="max-w-6xl mx-auto py-6 px-4 animate-slide-up">
            <div className="text-center mb-6">
              {/* Immagine Google Reviews */}
              <div className="mb-6">
                <img 
                  src={GOOGLE_REVIEWS_IMAGE} 
                  alt="Google Reviews - Lo studio più recensito in zona" 
                  className="mx-auto h-40 md:h-52 object-contain rounded-xl shadow-lg"
                />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Cosa dicono i clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[45vh] overflow-y-auto pr-2">
              {RECENSIONI.slice(0, 9).map((r, i) => (
                <div 
                  key={i}
                  className="bg-white/80 backdrop-blur rounded-xl p-4 border border-slate-200"
                >
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className="w-3 h-3 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-xs mb-3 italic line-clamp-3">"{r.testo}"</p>
                  <div className="border-t border-slate-200 pt-2">
                    <p className="text-slate-800 font-medium text-sm">{r.nome}</p>
                    <p className="text-slate-500 text-xs">{r.azienda}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "chiusura":
        return (
          <div key={key} className="flex flex-col items-center justify-center min-h-[85vh] text-center px-4 animate-slide-up">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-purple-500 flex items-center justify-center mb-5 mx-auto shadow-2xl">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-800 mb-4">
              Iniziamo insieme?
            </h2>
            
            {/* Messaggio chiave */}
            <div className="max-w-3xl mb-5 p-5 bg-gradient-to-r from-emerald-50 to-teal-100 rounded-2xl border border-emerald-200">
              <p className="text-slate-800 text-base mb-2">
                Da oggi non sei piu solo. Entri in un <strong className="text-emerald-700">sistema che:</strong>
              </p>
              <div className="grid md:grid-cols-3 gap-3 mt-3">
                <div className="text-center">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-2">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-700 text-xs font-medium">Ti toglie le incombenze<br/>con serenita</p>
                </div>
                <div className="text-center">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-2">
                    <BarChart3 className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-700 text-xs font-medium">Ti da il controllo<br/>della tua azienda</p>
                </div>
                <div className="text-center">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-700 text-xs font-medium">Anticipa ogni<br/>tua necessita</p>
                </div>
              </div>
            </div>
            
            {/* Pulsanti: Accetta + Richiedi Upgrade */}
            <div className="flex flex-col sm:flex-row gap-4 mb-5 max-w-2xl w-full">
              <div className="flex-1 text-center">
                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-lg px-8 py-6 font-bold shadow-2xl rounded-xl"
                  onClick={() => setShowAcceptDialog(true)}
                  data-testid="btn-accetta-preventivo"
                >
                  <FileSignature className="w-5 h-5 mr-2" />
                  Accetta Preventivo
                </Button>
                <p className="text-slate-500 text-xs mt-2">Riceverai via email i documenti da firmare per avviare l'incarico</p>
              </div>
              
              {!hasConsulenza && !hasPannelloControllo && (
                <div className="flex-1 text-center">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-amber-400 text-amber-700 hover:bg-amber-50 text-lg px-8 py-6 font-bold rounded-xl"
                    onClick={() => {
                      if (!upgradeRequested) {
                        setShowUpgradeDialog(true);
                      }
                    }}
                    disabled={upgradeRequested}
                    data-testid="btn-richiedi-upgrade"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {upgradeRequested ? 'Upgrade richiesto!' : 'Voglio un upgrade'}
                  </Button>
                  <p className="text-slate-500 text-xs mt-2">
                    {upgradeRequested 
                      ? 'Ti invieremo una nuova proposta aggiornata' 
                      : 'Riceverai una nuova proposta con i servizi aggiuntivi selezionati'}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-4 mb-5">
              <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 font-medium" onClick={() => handleShare('copy')} data-testid="btn-share-copy">
                <Lock className="w-4 h-4 mr-2" />Copia Link
              </Button>
            </div>
            
            <div className="text-slate-400 text-xs">
              <p className="mb-3">Preventivo n. {preventivo.numero_preventivo}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-slate-200 max-w-2xl mx-auto">
                <p className="text-slate-600 font-semibold mb-1">SC Dottori Commercialisti SRL STP</p>
                <p className="text-slate-500 text-xs">Ravenna - Forli - Cervia - Roma - Bari - Cagliari</p>
              </div>
            </div>
          </div>
        );

      // ==================== SLIDE PER CLIENTI ESISTENTI ====================
      
      case "copertina_esistente":
        return (
          <div key={key} className="flex flex-col items-center justify-center min-h-[85vh] text-center px-4">
            <div className="animate-slide-up">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              
              <p className="text-blue-500 text-lg mb-4 tracking-widest uppercase font-medium">
                Nuova Proposta di Servizi
              </p>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 tracking-tight">
                Preventivo n. {preventivo.numero_preventivo || 'Draft'}
              </h1>
              
              <div className="mt-6 mb-8">
                <div className="inline-block px-8 py-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-sm">
                  <p className="text-slate-600 text-sm mb-1">Preparato per</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
                    {preventivo.cliente.ragione_sociale}
                  </h2>
                  {preventivo.cliente.partita_iva && (
                    <p className="text-slate-500 text-sm mt-1">P.IVA {preventivo.cliente.partita_iva}</p>
                  )}
                </div>
              </div>
              
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Di seguito trovi il dettaglio dei servizi richiesti e le relative condizioni economiche.
              </p>
            </div>
          </div>
        );
        
      case "riepilogo_servizi":
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Servizi Richiesti
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Ecco i servizi che hai selezionato per la tua attività
            </p>
            
            <div className="grid gap-4">
              {preventivo.servizi.map((servizio, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Check className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{servizio.nome}</p>
                      {servizio.descrizione && (
                        <p className="text-slate-500 text-sm">{servizio.descrizione.substring(0, 80)}...</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-slate-800">
                      {servizio.prezzo_totale?.toLocaleString('it-IT', {minimumFractionDigits: 2})} €
                    </p>
                    <p className="text-slate-500 text-sm">
                      {servizio.billing_type === 'monthly' ? '/anno' : 'una tantum'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case "dettaglio_servizi":
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Cosa Include Ogni Servizio
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Nel dettaglio, ecco cosa faremo per te
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {preventivo.servizi.slice(0, 6).map((servizio, idx) => (
                <div 
                  key={idx} 
                  className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-5 border border-slate-200"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{idx + 1}</span>
                    </div>
                    <h3 className="font-bold text-slate-800">{servizio.nome}</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600 ml-11">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Gestione completa dell'adempimento</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Assistenza dedicata</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>Rispetto delle scadenze garantito</span>
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );
        
      case "tempistiche":
        return (
          <div key={key} className="max-w-4xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Tempistiche e Modalità
            </h2>
            <p className="text-slate-500 text-center mb-10">
              Come procederemo insieme
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">Avvio immediato</h3>
                  <p className="text-slate-600">
                    Alla firma dei documenti, attiveremo subito i servizi concordati. 
                    Non ci sono tempi di attesa.
                  </p>
                </div>
              </div>
              
              {/* Pagamento mensile - solo se ci sono servizi ricorrenti */}
              {hasServiziRicorrenti && (
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-lg">Pagamento mensile</h3>
                    <p className="text-slate-600">
                      Il canone mensile di <strong>{(nettoMensile).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong> + Cassa prev. + IVA 
                      viene addebitato entro il {preventivo.giorno_incasso || 10} di ogni mese tramite RID o bonifico.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Pagamento una tantum - solo se ci sono servizi one_off e NON ci sono ricorrenti */}
              {!hasServiziRicorrenti && totaleUnaTantum > 0 && (
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-lg">Pagamento prima dell'invio delle pratiche</h3>
                    <p className="text-slate-600">
                      L'importo di <strong>{(totaleUnaTantum).toLocaleString('it-IT', {minimumFractionDigits: 2})} €</strong> + Cassa prev. 4% + IVA 22%
                      sarà fatturato all'accettazione del preventivo.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Durata e rinnovo - solo se ci sono servizi ricorrenti */}
              {hasServiziRicorrenti && (
                <div className="flex items-start gap-4 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-lg">Durata e rinnovo</h3>
                    <p className="text-slate-600">
                      Contratto biennale iniziale con rinnovo tacito annuale. 
                      Disdetta con 6 mesi di preavviso.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      // =====================================================
      // NUOVE SLIDE PER LE 4 TIPOLOGIE
      // =====================================================
      
      case "dettaglio_pratiche":
        // Slide per SOLO PRATICHE - mostra dettaglio dei servizi una tantum
        return (
          <div key={key} className="max-w-4xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Pratiche Richieste
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Dettaglio dei servizi che gestiremo per te
            </p>
            
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="p-6">
                <div className="space-y-4">
                  {preventivo.servizi.map((s, i) => (
                    <div key={i} className="flex justify-between items-start p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800">{s.nome}</h4>
                        {s.descrizione_utente && (
                          <p className="text-sm text-slate-500 mt-1">{s.descrizione_utente}</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-bold text-lg text-slate-800">{formatCurrency(s.prezzo_totale)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 border-t border-amber-200">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 font-medium">Totale pratiche</span>
                  <span className="text-2xl font-bold text-slate-800">
                    {formatCurrency(preventivo.servizi.reduce((acc, s) => acc + s.prezzo_totale, 0))}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">+ Cassa prev. 4% + IVA 22%</p>
              </div>
            </div>
          </div>
        );

      case "incluso_contabilita":
        // Slide per SOLO CONTABILITÀ - cosa è incluso nel servizio
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Cosa è Incluso nel Servizio
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Tutto ciò che gestiamo per te con il canone mensile
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Contabilità e Adempimenti */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Calculator className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">Contabilità e Adempimenti</h3>
                <ul className="space-y-3">
                  {[
                    "Registrazioni contabili (fatture, pagamenti, incassi)",
                    "Liquidazioni IVA mensili/trimestrali",
                    "Predisposizione F24 e invio telematico",
                    "Dichiarazione IVA annuale",
                    "Bilancio e nota integrativa",
                    "Dichiarazione dei redditi"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Assistenza e Supporto */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">Assistenza e Supporto</h3>
                <ul className="space-y-3">
                  {[
                    "Referente dedicato per la tua azienda",
                    "Risposte rapide via email e WhatsApp",
                    "Consulenza fiscale ordinaria inclusa",
                    "Gestione rapporti con Agenzia delle Entrate",
                    "Alert scadenze e pagamenti",
                    "Accesso al portale documenti"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case "scaglioni":
        // Slide SCAGLIONI - mostra come funzionano gli adeguamenti
        return (
          <div key={key} className="max-w-4xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Trasparenza sui Costi
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Il canone si adegua automaticamente al volume di lavoro
            </p>
            
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
              <div className="p-6 bg-gradient-to-r from-blue-50 to-sky-50 border-b border-slate-200">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Come funziona?</h3>
                <p className="text-slate-600">
                  Il tuo canone include una <strong>soglia di registrazioni annue</strong>. 
                  Se la tua attività cresce e superi la soglia, il canone passa allo scaglione successivo. 
                  <strong className="text-blue-600"> Nessuna sorpresa a fine anno</strong> - ti avvisiamo sempre prima.
                </p>
              </div>
              
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-700">Scaglione</th>
                    <th className="text-right p-4 font-semibold text-slate-700">Canone annuo</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { range: "Fino a 600 registrazioni", prezzo: "2.700 €" },
                    { range: "Da 601 a 1.200 registrazioni", prezzo: "3.600 €" },
                    { range: "Da 1.201 a 2.000 registrazioni", prezzo: "4.800 €" },
                    { range: "Da 2.001 a 3.000 registrazioni", prezzo: "6.000 €" },
                    { range: "Oltre 3.000 registrazioni", prezzo: "Su misura" }
                  ].map((row, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-slate-600">{row.range}</td>
                      <td className="p-4 text-right font-semibold text-slate-800">{row.prezzo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <p className="text-emerald-800 text-center">
                <Lightbulb className="w-5 h-5 inline mr-2" />
                <strong>Il tuo preventivo parte dallo scaglione più adatto</strong> alla tua attività attuale.
                Se cresci, cresciamo insieme!
              </p>
            </div>
          </div>
        );

      case "sistema_completo":
        // Slide per PACCHETTO COMPLETO - il sistema per blindare l'azienda
        return (
          <div key={key} className="max-w-5xl mx-auto py-8 px-4 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2 text-center">
              Il Sistema Completo per la Tua Azienda
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Non solo contabilità, ma un vero partner strategico
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Pilastro 1: Adempimenti */}
              <div className="bg-gradient-to-br from-blue-50 to-sky-100 rounded-2xl p-6 border border-blue-200">
                <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center mb-4 shadow-lg">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Adempimenti Blindati</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Tutte le scadenze fiscali e contabili gestite con precisione. Mai più ritardi o sanzioni.
                </p>
                <ul className="space-y-2 text-sm">
                  {["Contabilità", "Dichiarazioni", "F24 e scadenze"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-blue-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Pilastro 2: Controllo */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 rounded-2xl p-6 border border-emerald-200">
                <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center mb-4 shadow-lg">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Controllo Totale</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Sai sempre come sta andando la tua azienda. Numeri chiari, decisioni informate.
                </p>
                <ul className="space-y-2 text-sm">
                  {["KPI in tempo reale", "Margini e cassa", "Simulazioni fiscali"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Pilastro 3: Strategia */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-100 rounded-2xl p-6 border border-violet-200">
                <div className="w-14 h-14 rounded-xl bg-violet-500 flex items-center justify-center mb-4 shadow-lg">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Strategia Proattiva</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Non aspettiamo i problemi, li anticipiamo. Pianificazione fiscale e obiettivi condivisi.
                </p>
                <ul className="space-y-2 text-sm">
                  {["Riunioni trimestrali", "Piano fiscale", "Early warning"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-violet-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-center">
              <p className="text-white text-lg">
                <Sparkles className="w-5 h-5 inline mr-2 text-amber-400" />
                Con questo sistema, <strong className="text-amber-400">la tua azienda è blindata</strong>: 
                adempimenti in regola, numeri sotto controllo, decisioni strategiche supportate dai dati.
              </p>
            </div>
          </div>
        );

      // =====================================================
      // NUOVE SLIDE: PROFESSIONISTA, DETTAGLIO INCLUSO, DIFFERENZIAZIONE, TECNOLOGIA
      // =====================================================

      case "il_tuo_commercialista":
        return (
          <div key={key} className="max-w-5xl mx-auto py-6 px-4 animate-slide-up">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
                Un team dedicato, un metodo collaudato
              </h2>
              <p className="text-lg text-slate-500">Non sei un numero. Sei un'azienda che seguiamo da vicino, ogni mese.</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 items-start">
              {/* Il nostro metodo */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
                <div className="flex items-center gap-4 mb-5 pb-4 border-b border-slate-100">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">Il nostro approccio</h3>
                    <p className="text-blue-600 text-sm font-medium">Team strutturato + controllo professionale</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Un Dottore Commercialista guarda i tuoi numeri</p>
                      <p className="text-slate-500 text-sm">Laureato con 110 e lode, con oltre 20 anni di esperienza. Ogni mese controlla personalmente l'andamento della tua azienda.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Segnalazioni tempestive</p>
                      <p className="text-slate-500 text-sm">Se qualcosa non torna o c'è un'opportunità, ti avvisiamo subito. Non aspettiamo fine anno.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <HeartHandshake className="w-4 h-4 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Un team che lavora per te</p>
                      <p className="text-slate-500 text-sm">Dietro al tuo Commercialista c'è un team operativo specializzato: contabilità, fiscale, consulenza. Ognuno nel suo ruolo.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Referente fisso, sempre lo stesso</p>
                      <p className="text-slate-500 text-sm">Non cambiamo mai chi ti segue. Chi conosce la tua azienda è chi prende le decisioni con te.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Cosa succede negli altri studi */}
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-200">
                  <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-500 text-sm uppercase tracking-wider">Negli altri studi</p>
                    <p className="text-xs text-slate-400">Quello che succede nel 90% dei casi</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100">
                    <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 text-sm font-medium">Ti cambiano il referente di continuo</p>
                      <p className="text-slate-400 text-xs">Ogni volta devi rispiegare tutto da capo</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100">
                    <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 text-sm font-medium">Il commercialista non guarda i tuoi numeri</p>
                      <p className="text-slate-400 text-xs">Firma il bilancio a fine anno senza averlo mai analizzato durante l'anno</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100">
                    <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 text-sm font-medium">Persone senza qualifiche specifiche</p>
                      <p className="text-slate-400 text-xs">Chi ti segue spesso è un impiegato, non un Dottore Commercialista</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100">
                    <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-slate-700 text-sm font-medium">Nessuna segnalazione proattiva</p>
                      <p className="text-slate-400 text-xs">Ti chiamano solo per le scadenze o quando c'è un problema</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "cosa_include_dettaglio":
        return (
          <div key={key} className="max-w-6xl mx-auto py-4 px-4 animate-slide-up">
            <div className="text-center mb-5">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
                Ecco cosa è incluso nel tuo preventivo
              </h2>
              <p className="text-base text-slate-500">
                Ogni servizio spiegato nel dettaglio, con il valore reale che porta alla tua azienda
              </p>
            </div>
            
            <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300">
              {preventivo.servizi.map((s, i) => {
                const info = getServizioDescrizione(s.nome);
                const isContabilita = s.categoria === 'contabilita' && (
                  s.nome.toLowerCase().includes('contabilità') || 
                  s.nome.toLowerCase().includes('contabilita')
                );
                return (
                  <div key={i}>
                    <div 
                      className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm opacity-0 animate-fade-in-up"
                      style={{ animationDelay: `${0.1 + i * 0.08}s`, animationFillMode: 'forwards' }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            info.obbligo ? 'bg-blue-100' : 'bg-violet-100'
                          }`}>
                            {info.obbligo 
                              ? <ShieldCheck className="w-4 h-4 text-blue-600" />
                              : <Sparkles className="w-4 h-4 text-violet-600" />
                            }
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-slate-800">{s.nome}</h4>
                              {info.obbligo && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-full border border-blue-200 uppercase tracking-wider">
                                  Obbligo di legge
                                </span>
                              )}
                              {!info.obbligo && (
                                <span className="px-2 py-0.5 bg-violet-50 text-violet-700 text-[10px] font-bold rounded-full border border-violet-200 uppercase tracking-wider">
                                  Valore aggiunto
                                </span>
                              )}
                            </div>
                            <p className="text-slate-500 text-sm mt-1 leading-relaxed">{info.desc}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-slate-800 font-mono text-sm">{formatCurrency(s.prezzo_totale)}</p>
                          <p className="text-slate-400 text-xs">{s.billing_type === 'monthly' ? '/anno' : 'una tantum'}</p>
                        </div>
                      </div>
                      
                      {/* Scaglioni contabilità */}
                      {isContabilita && scaglioniContabilita.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Tutti gli scaglioni — Contabilità {tipoContabilita.charAt(0).toUpperCase() + tipoContabilita.slice(1)}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {scaglioniContabilita.map((scaglione, idx) => {
                              const isAttuale = scaglione.nome === s.nome || scaglione.id === s.id;
                              const prezzo = scaglione.prezzo_annuale || scaglione.prezzo_default;
                              const prezzoUnitario = scaglione.prezzo_unitario;
                              // Nome corto: rimuovi prefisso ripetitivo
                              const nomeCorto = scaglione.nome
                                .replace(/Contabilità Ordinaria /i, '')
                                .replace(/Contabilità Semplificata /i, '')
                                .replace(/Regime Forfettario /i, '');
                              return (
                                <div
                                  key={idx}
                                  className={`px-3 py-2 rounded-lg text-xs border transition-all ${
                                    isAttuale
                                      ? 'bg-blue-50 border-blue-300 text-blue-800 font-bold ring-1 ring-blue-200'
                                      : 'bg-slate-50 border-slate-200 text-slate-600'
                                  }`}
                                >
                                  <div className="font-medium">{nomeCorto}</div>
                                  <div className="font-mono mt-0.5">
                                    {prezzo ? `${formatCurrency(prezzo)}/anno` : prezzoUnitario ? `${formatCurrency(prezzoUnitario)}/cad.` : ''}
                                  </div>
                                  {isAttuale && <div className="text-blue-600 text-[10px] mt-0.5 font-bold">IL TUO SCAGLIONE</div>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 text-center">
              <p className="text-emerald-800 text-sm">
                <ShieldCheck className="w-4 h-4 inline mr-1" />
                Questa è la soluzione <strong>più adatta alle tue esigenze attuali</strong>. In qualsiasi momento potrai aggiungere o modificare i servizi.
              </p>
            </div>
          </div>
        );

      case "noi_vs_altri":
        return (
          <div key={key} className="max-w-6xl mx-auto py-4 px-4 animate-slide-up">
            <div className="text-center mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
                Perché scegliere noi
              </h2>
              <p className="text-base text-slate-500">La differenza tra un commercialista generico e un partner strategico</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              {/* LORO */}
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
                  <div className="w-9 h-9 bg-slate-300 rounded-full flex items-center justify-center">
                    <X className="w-4 h-4 text-slate-500" />
                  </div>
                  <p className="font-bold text-slate-500 text-sm uppercase tracking-wider">Commercialista generico</p>
                </div>
                <div className="space-y-3">
                  {[
                    { title: "Solo adempimenti", desc: "Registra, dichiara, deposita. Fine." },
                    { title: "Bilanci vecchi di mesi", desc: "I numeri li vedi una volta l'anno, quando ormai è tardi." },
                    { title: "Referente che cambia", desc: "Ogni volta una persona diversa che non conosce la tua storia." },
                    { title: "Zero proattività", desc: "Ti chiama solo quando c'è una scadenza o un problema." },
                    { title: "Sorprese a fine anno", desc: "\"Quest'anno devi pagare X\" - scoperto a novembre." },
                    { title: "Tecnologia assente", desc: "Fogli Excel, email con allegati, nessuna piattaforma." },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-600 text-sm font-medium">{item.title}</p>
                        <p className="text-slate-400 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* NOI */}
              <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-5 border-2 border-blue-400 shadow-lg relative">
                <div className="absolute -top-3 right-4 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                  SC Dottori Commercialisti
                </div>
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-blue-200">
                  <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="font-bold text-blue-800 text-sm uppercase tracking-wider">Il nostro approccio</p>
                </div>
                <div className="space-y-3">
                  {[
                    { title: "Adempimenti + visione strategica", desc: "Non ci limitiamo a fare i conti: ti aiutiamo a prendere decisioni migliori." },
                    { title: "Bilanci sempre aggiornati", desc: "Sai in tempo reale come sta andando la tua azienda, non a fine anno." },
                    { title: "Il Commercialista guarda i tuoi numeri", desc: "Ogni mese un professionista con 20 anni di esperienza controlla l'andamento e ti segnala cosa fare." },
                    { title: "Assistenza proattiva", desc: "Se qualcosa non va, te lo segnaliamo noi prima che diventi un problema." },
                    { title: "Pianificazione fiscale", desc: "Sai già a metà anno quanto pagherai di tasse. Zero sorprese, solo scelte consapevoli." },
                    { title: "Piattaforma digitale", desc: "Dati in tempo reale, fatture automatiche via SPID, documenti sempre accessibili." },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-800 text-sm font-medium">{item.title}</p>
                        <p className="text-slate-500 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-4 text-center">
              <p className="text-white text-sm md:text-base">
                <HeartHandshake className="w-5 h-5 inline mr-2 text-blue-400" />
                Non cerchiamo clienti. Cerchiamo <strong className="text-blue-400">imprenditori che vogliono il controllo</strong> della propria azienda.
              </p>
            </div>
          </div>
        );

      case "tecnologia":
        return (
          <div key={key} className="max-w-5xl mx-auto py-6 px-4 animate-slide-up">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                <Wifi className="w-4 h-4" />
                Tecnologia al servizio della semplicità
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
                Dati in tempo reale, zero complicazioni
              </h2>
              <p className="text-base text-slate-500">Digitalizziamo tutto per farti risparmiare tempo e darti il controllo</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                    <MonitorSmartphone className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Fatture elettroniche automatiche</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  Con una semplice <strong>delega SPID</strong>, riceviamo automaticamente tutte le tue fatture. Non devi più inviarci nulla: arrivano direttamente a noi.
                </p>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-emerald-700 text-xs font-medium">
                    <Check className="w-3 h-3 inline mr-1" />
                    Risultato: zero lavoro manuale per te sulle fatture
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Bilanci sempre aggiornati</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  Grazie all'automazione, i tuoi dati contabili sono <strong>sempre aggiornati</strong>. Non devi aspettare fine anno per sapere come sta andando la tua azienda.
                </p>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-700 text-xs font-medium">
                    <Check className="w-3 h-3 inline mr-1" />
                    Risultato: decisioni basate su dati reali, non sensazioni
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Collegamento bancario</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  I movimenti bancari arrivano in automatico tramite <strong>Sportello Cloud</strong>. Noi li contabilizziamo, tu ti concentri sul business.
                </p>
                <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-violet-700 text-xs font-medium">
                    <Check className="w-3 h-3 inline mr-1" />
                    Risultato: contabilità precisa senza rincorrere estratti conto
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Pianificazione fiscale continua</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  Con dati aggiornati in tempo reale, possiamo <strong>stimare le imposte durante l'anno</strong> e suggerirti le azioni migliori prima delle scadenze.
                </p>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-700 text-xs font-medium">
                    <Check className="w-3 h-3 inline mr-1" />
                    Risultato: zero sorprese, solo scelte consapevoli
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl text-center">
              <p className="text-white text-sm md:text-base font-medium">
                <Lightbulb className="w-5 h-5 inline mr-2 text-amber-300" />
                La tecnologia non sostituisce il professionista: lo <strong>potenzia</strong>. Tu hai meno lavoro, noi abbiamo dati migliori per consigliarti.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="presentation-professional">
      
      {/* Light, elegant gradient background - professional and bright */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-sky-100" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-200/40 via-sky-100/30 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-200/30 via-blue-100/20 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-indigo-200/25 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-teal-200/20 via-transparent to-transparent" />
      
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>
      
      {/* Content */}
      <div className="relative z-10 h-screen flex flex-col overflow-hidden">
        {/* Studio Logo - fixed header - nascosto su mobile per evitare sovrapposizioni */}
        <div className="hidden md:block absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
          <span className="text-lg md:text-xl font-bold text-slate-700 bg-white/80 px-4 py-2 rounded-lg shadow-sm">
            SC Dottori Commercialisti
          </span>
        </div>
        
        {/* Top bar - riorganizzata per mobile */}
        <div data-testid="presentation-controls" className="flex-shrink-0 flex items-center justify-between p-2 md:p-4">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Logo su mobile nella top bar */}
            <span className="md:hidden text-xs font-bold text-slate-700 bg-white/80 px-2 py-1 rounded-lg shadow-sm">
              SC Dottori Commercialisti
            </span>
            <div className="text-slate-600 text-xs md:text-sm bg-white/70 px-2 md:px-4 py-1 md:py-1.5 rounded-full font-medium shadow-sm">
              {currentSlide + 1} / {slides.length}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAutoPlay(!isAutoPlay);
                setProgressKey(prev => prev + 1);
              }}
              className="hidden md:flex text-slate-600 hover:text-slate-900 hover:bg-white/50"
              data-testid="btn-autoplay"
            >
              {isAutoPlay ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              {isAutoPlay ? 'Pausa' : 'Auto'}
            </Button>
          </div>
          
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900 hover:bg-white/50 px-2 md:px-3"
              onClick={() => setShowShareDialog(true)}
            >
              <Share2 className="w-4 h-4 md:mr-1" />
              <span className="hidden md:inline">Condividi</span>
            </Button>
            {!isPublicView && (
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-600 hover:text-slate-900 hover:bg-white/50 px-2 md:px-3"
                onClick={onClose}
                data-testid="btn-close-presentation"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Main content - con scroll orizzontale per contenuti larghi */}
        <div 
          ref={contentRef}
          className="flex-1 px-2 md:px-8 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
        >
          <div className="min-w-fit">
            {renderSlide()}
          </div>
          
          {/* Linguette laterali per suggerire la navigazione swipe */}
          {currentSlide > 0 && (
            <button
              onClick={() => { setIsAutoPlay(false); prevSlide(); }}
              className="fixed left-0 top-1/2 -translate-y-1/2 z-20 group"
              data-testid="swipe-hint-left"
              aria-label="Slide precedente"
            >
              <div className="flex items-center bg-white/60 backdrop-blur-sm border border-slate-200/80 rounded-r-xl py-5 pl-1 pr-2 shadow-sm group-hover:bg-white/90 group-hover:shadow-md transition-all group-active:scale-95">
                <ChevronLeft className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </button>
          )}
          {currentSlide < slides.length - 1 && (
            <button
              onClick={() => { setIsAutoPlay(false); nextSlide(); }}
              className="fixed right-0 top-1/2 -translate-y-1/2 z-20 group animate-pulse-gentle"
              data-testid="swipe-hint-right"
              aria-label="Slide successiva"
            >
              <div className="flex items-center bg-white/60 backdrop-blur-sm border border-slate-200/80 rounded-l-xl py-5 pr-1 pl-2 shadow-sm group-hover:bg-white/90 group-hover:shadow-md transition-all group-active:scale-95">
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </button>
          )}
        </div>
        
        {/* Navigation - ottimizzata per mobile */}
        <div data-testid="presentation-nav" className="flex-shrink-0 flex items-center justify-between p-2 md:p-4 lg:p-6 pb-16 md:pb-6 bg-gradient-to-t from-slate-100/80 to-transparent">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="lg"
              className={`text-slate-700 hover:bg-white/50 px-2 md:px-4 bg-white/70 shadow-sm ${currentSlide === 0 ? 'invisible' : ''}`}
              onClick={() => { setIsAutoPlay(false); prevSlide(); }}
              data-testid="btn-prev-slide"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 md:mr-2" />
              <span className="hidden md:inline">Indietro</span>
            </Button>
            {/* Pausa/Play mobile */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAutoPlay(!isAutoPlay);
                setProgressKey(prev => prev + 1);
              }}
              className="md:hidden text-slate-600 hover:text-slate-900 bg-white/70 shadow-sm px-2"
              data-testid="btn-autoplay-mobile"
            >
              {isAutoPlay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Progress bar - più compatta su mobile */}
          <div className="flex gap-1 md:gap-1.5 max-w-[40%] md:max-w-none overflow-x-auto">
            {slides.map((s, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (!isAnimating) {
                    setIsAutoPlay(false);
                    setIsAnimating(true);
                    setCurrentSlide(idx);
                    setSlideKey(prev => prev + 1);
                    setProgressKey(prev => prev + 1);
                    setTimeout(() => setIsAnimating(false), 600);
                  }
                }}
                title={s.title}
                className={`h-1.5 md:h-2 rounded-full transition-all flex-shrink-0 ${
                  idx === currentSlide 
                    ? 'bg-blue-600 w-6 md:w-8' 
                    : idx < currentSlide 
                      ? 'bg-blue-400 w-1.5 md:w-2'
                      : 'bg-slate-300 w-1.5 md:w-2 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
          
          <Button
            variant="ghost"
            size="lg"
            className={`text-slate-800 hover:bg-white/10 px-2 md:px-4 bg-white/70 shadow-sm ${currentSlide === slides.length - 1 ? 'invisible' : ''}`}
            onClick={() => { setIsAutoPlay(false); nextSlide(); }}
            data-testid="btn-next-slide"
          >
            <span className="hidden md:inline">Avanti</span>
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 md:ml-2" />
          </Button>
        </div>
        
        {/* Auto-play progress bar */}
        {isAutoPlay && currentSlide < slides.length - 1 && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 print:hidden">
            <div className="h-1 w-40 bg-white/10 rounded-full overflow-hidden">
              <div 
                key={progressKey}
                className="h-full bg-blue-400/60 rounded-full"
                style={{ 
                  animation: `progress ${SLIDE_DURATION}ms linear`,
                  width: '0%'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Dialog per Accettazione Preventivo - Invio Documenti via Email */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-emerald-600" />
              Accetta il Preventivo
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Inserisci i dati per ricevere i documenti da firmare via email
            </DialogDescription>
          </DialogHeader>
          
          {!otpVerified ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="signer_first_name" className="text-slate-700 font-medium">Nome *</Label>
                <Input
                  id="signer_first_name"
                  placeholder="Mario"
                  value={signerFirstName}
                  onChange={(e) => setSignerFirstName(e.target.value)}
                  className="border-slate-300"
                  data-testid="input-signer-first-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signer_last_name" className="text-slate-700 font-medium">Cognome *</Label>
                <Input
                  id="signer_last_name"
                  placeholder="Rossi"
                  value={signerLastName}
                  onChange={(e) => setSignerLastName(e.target.value)}
                  className="border-slate-300"
                  data-testid="input-signer-last-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="acceptance_email" className="text-slate-700 font-medium">Email *</Label>
                <Input
                  id="acceptance_email"
                  type="email"
                  placeholder="mario.rossi@email.com"
                  value={acceptanceEmail}
                  onChange={(e) => setAcceptanceEmail(e.target.value)}
                  className="border-slate-300"
                  data-testid="input-acceptance-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signer_cf" className="text-slate-700 font-medium">Codice Fiscale del firmatario *</Label>
                <Input
                  id="signer_cf"
                  placeholder="RSSMRA80A01H501X"
                  value={signerCF}
                  onChange={(e) => setSignerCF(e.target.value.toUpperCase())}
                  maxLength={16}
                  className="border-slate-300 uppercase"
                  data-testid="input-signer-cf"
                />
                <p className="text-xs text-slate-500">Il CF del firmatario verrà inserito nei documenti</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-amber-800 font-medium text-sm">Come funziona</p>
                    <p className="text-amber-700 text-xs mt-1">
                      Riceverai via email tutti i documenti con le istruzioni su dove firmare.
                      Dopo averli firmati, dovrai rispondere alla email allegando i documenti firmati.
                    </p>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAcceptDialog(false)}
                  disabled={isSubmitting}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleSendForSignature}
                  disabled={isSubmitting || !signerFirstName || !signerLastName || !acceptanceEmail || !signerCF || signerCF.length !== 16}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-send-for-signature"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Invia Documenti
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Documenti Inviati!</h3>
              <p className="text-slate-600 mb-4">
                Abbiamo inviato tutti i documenti a:<br/>
                <strong className="text-slate-800">{acceptanceEmail}</strong>
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-left">
                <p className="text-blue-800 font-medium text-sm mb-2">Prossimi passi:</p>
                <ol className="text-blue-700 text-sm space-y-1 list-decimal list-inside">
                  <li>Controlla la tua casella email (anche spam)</li>
                  <li>Stampa la Lettera d'Incarico</li>
                  <li>Firma nei punti indicati</li>
                  <li>Scansiona e rispondi alla email allegando il documento firmato</li>
                </ol>
              </div>
              
              <DialogFooter className="mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAcceptDialog(false);
                    setOtpVerified(false);
                  }}
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog Condivisione */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Condividi Presentazione
            </DialogTitle>
            <DialogDescription>
              Invia il link della presentazione al cliente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Link da copiare */}
            <div className="flex items-center gap-2">
              <Input 
                value={publicUrl} 
                readOnly 
                className="text-sm"
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(publicUrl);
                  toast.success("Link copiato!");
                }}
              >
                Copia
              </Button>
            </div>
            
            {/* Pulsanti condivisione */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="flex items-center gap-2 h-12"
                onClick={() => {
                  handleShare('whatsapp');
                  setShowShareDialog(false);
                }}
              >
                <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </Button>
              
              <Button
                variant="outline"
                className="flex items-center gap-2 h-12"
                onClick={() => {
                  handleShare('email');
                  setShowShareDialog(false);
                }}
              >
                <Mail className="w-5 h-5 text-blue-600" />
                Email
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              Il cliente potrà visualizzare la presentazione sul suo dispositivo
            </p>
          </div>
        </DialogContent>
      </Dialog>
      

      {/* Dialog Richiesta Upgrade */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Quali servizi ti interessano?</DialogTitle>
            <DialogDescription>Puoi selezionare anche entrambi. Ti invieremo una nuova proposta aggiornata.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <button
              className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                selectedUpgrades?.includes('consulenza_strategica') 
                  ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-300' 
                  : 'bg-amber-50 hover:bg-amber-100 border-amber-200 hover:border-amber-400'
              }`}
              onClick={() => {
                setSelectedUpgrades(prev => {
                  const current = prev || [];
                  return current.includes('consulenza_strategica') 
                    ? current.filter(u => u !== 'consulenza_strategica')
                    : [...current, 'consulenza_strategica'];
                });
              }}
              data-testid="btn-upgrade-strategico"
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  selectedUpgrades?.includes('consulenza_strategica') ? 'bg-amber-500 border-amber-500' : 'border-slate-300'
                }`}>
                  {selectedUpgrades?.includes('consulenza_strategica') && <Check className="w-3 h-3 text-white" />}
                </div>
                <Target className="w-8 h-8 text-amber-600" />
                <div>
                  <p className="font-bold text-slate-800">Consulenza Strategica</p>
                  <p className="text-sm text-slate-500">Margini, cliente target, strategie di crescita, pianificazione fiscale</p>
                </div>
              </div>
            </button>
            <button
              className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                selectedUpgrades?.includes('pannello_direzionale') 
                  ? 'bg-slate-700 border-amber-400 ring-2 ring-amber-300' 
                  : 'bg-slate-800 hover:bg-slate-700 border-amber-400'
              }`}
              onClick={() => {
                setSelectedUpgrades(prev => {
                  const current = prev || [];
                  return current.includes('pannello_direzionale') 
                    ? current.filter(u => u !== 'pannello_direzionale')
                    : [...current, 'pannello_direzionale'];
                });
              }}
              data-testid="btn-upgrade-totale"
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  selectedUpgrades?.includes('pannello_direzionale') ? 'bg-amber-500 border-amber-500' : 'border-slate-500'
                }`}>
                  {selectedUpgrades?.includes('pannello_direzionale') && <Check className="w-3 h-3 text-white" />}
                </div>
                <BarChart3 className="w-8 h-8 text-amber-400" />
                <div>
                  <p className="font-bold text-white">Software Pannello di Controllo Direzionale</p>
                  <p className="text-sm text-slate-400">Tutto + Software con semafori, KPI e obiettivi a cascata</p>
                </div>
              </div>
            </button>
            
            <Button
              className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3"
              disabled={!selectedUpgrades?.length}
              onClick={() => {
                handleRequestUpgrade(selectedUpgrades.join(' + '));
                setShowUpgradeDialog(false);
              }}
              data-testid="btn-conferma-upgrade"
            >
              <Send className="w-4 h-4 mr-2" />
              {selectedUpgrades?.length 
                ? `Richiedi ${selectedUpgrades.length === 2 ? 'entrambi' : 'upgrade'}`
                : 'Seleziona almeno un servizio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes slide-up {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(16px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
